import { KMLDocument, KMLElement } from '../kml';
import { MissionLoader, MissionChecker, isSampleMicro, type MissionCSV } from '@rogoag/shared-ts';
import { Company } from '@rogoag/airtable/crm';
import { fromLatLon, toLatLon } from 'utm';
import download from 'downloadjs';
import { DownloadOptions, ZipOptions, zip as shpZip } from '@mapbox/shp-write';
import { getStyleText } from '../style';
import { getVersionNumber } from '../version';
import type { Feature, Geometry, GeoJsonProperties } from 'geojson';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import {
  CorePoint,
  GridPatterns,
  Job,
  Mission,
  PathPoint,
  Sample,
  SampleBox,
  SampleCentroid,
  SampleSite,
  SamplingSpec,
  SamplingTypes,
  SoilCore,
  SoilDumpEvent,
  Waypoint,
  Zone,
  ZoneType,
} from '../db';
import type shp from 'shpjs';
import { cleanSet, isclose, formatDateAsString, uuidv4, calcLineDistance, arraysEqualUnordered } from '../utils';
import { clearAllBearings, clearBearings, clearBearingsAround } from './waypoint_bearings';

// KML ExtendedData key on the path Placemark: JSON object waypoint_number →
// bearing_azimuth_deg for the pinned waypoints (see _to_kml / MissionKmlLoader).
export const WAYPOINT_BEARINGS_KML_KEY = 'waypointBearingsAzimuthDeg';
import { metresPerDegreeLat, metresPerDegreeLon } from '../services/paths/robotBearing';
import {
  FEET_BETWEEN_CORE_LINES_FOR_MULTI_LAB,
  ft_to_m,
  MAX_DISTANCE_BETWEEN_CLOSE_CORES_FT,
  MIDPOINT_DUMP_MIN_INCHES,
  CORE_STOP_MAX_DISTANCE_FT,
} from '../constants';
import { getCurrentMission } from '../dataModelHelpers';
import { flattenDeep, orderBy, flatten } from 'lodash';
import { alertError, alertInfo, alertWarn, alertWarnConfirm } from '../alertDispatcher';
import hash from 'object-hash';
import logger from '../logger';
import { dispatchMissionBoundaryUpdated, dispatchMissionUpdated } from '../missionEvents';
import { dispatchCoresNeedUpdate, dispatchStaticPointsUpdated } from '../mapEvents';
import {
  SampleChangeType,
  ConfirmColumn,
  CoreDiameter,
  RogoShapefile,
  RogoShapefileType,
  SampleSource,
  TakenCore,
  CoreSource,
  IntermediateWaypointsMap,
  BoundaryChangeType,
} from '../types/types';
import { FrameID, RosBoundary, RosMissionMsgWithFieldParam, RosSample, RosWaypoint } from '../types/rosmsg';
import type { FeatureCollection, MultiPolygon, Point, Polygon, Position } from 'geojson';
import { PRINTER_CONFIG } from '../components/robot/configs/PrinterConfig';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { Coordinate } from 'ol/coordinate';
import { KML_FEATURE_TYPE } from '../featureTypes';
import { BD_CORE_COMMAND, FieldParams, KM_PER_MILE, MM_PER_FT } from '../constants';
import { getCurrentSession } from '../dataModelHelpers';
import { genPdfCheckinOld } from '../pdfcheckin_old';
import { Job as AtJob, Deal as AtDeal, BarcodeType as AtBarcodeType, Lab as AtLab } from '@rogoag/airtable/crm';
import { MapCalculationPositionStorage, RobotArmOffsetX, RobotArmOffsetY } from '../db/local_storage';
import GridPathGenerator from '../services/GridPathGenerator';
import OriginalPointsOrZonesFileProcessor, {
  matchCore,
} from '../services/mission-loading/original/from-shapes/OriginalPointsOrZonesFileProcessor';
import { processMultipolygonCoords } from '../services/BoundaryService';
import { NavigationPoint } from '../services/paths/types';
import { setSampleAndCoreOrder } from '../services/paths/utils';
import { ZonePathGenerator } from '../services/ZonePathGenerator';
import { ShapefileParserService } from '../services/ShapefileParserService';
import { crmBase } from '../airtable_crm';
import { toIJob, toIDeal, toICompany } from '../services/lab-csv/missionCsvDtos';
import { seedZonesFromMapFeatures } from './map_features_ops';
// import { genBoundarySentToRobot } from '../utilities/rosUtils';

export function assign_id(this: Mission) {
  const outer_boundary_ids = cleanSet(flatten(this.getZones().map((sel) => sel.getOuterZoneBoundarys()))).map(
    (sel) => sel.boundary_id,
  );

  const inner_boundary_ids = cleanSet(flatten(this.getZones().map((sel) => sel.getInnerZoneBoundarys()))).map(
    (sel) => sel.boundary_id,
  );

  const outer_poly_ids = cleanSet(flatten(this.getZones().map((sel) => sel.getOuterZoneBoundarys()))).map(
    (sel) => sel.poly_id,
  );

  const inner_poly_ids = cleanSet(flatten(this.getZones().map((sel) => sel.getInnerZoneBoundarys()))).map(
    (sel) => sel.poly_id,
  );

  const all_ids = outer_boundary_ids.concat(inner_boundary_ids, outer_poly_ids, inner_poly_ids);

  return all_ids.length > 0 ? Math.max(...all_ids) + 1 : 1;
}

export function to_kml(this: Mission, { use_pulled_locations = false, insert_boxes = true, safe_mode = false }) {
  let success = false;

  try {
    const kmlDoc = this._to_kml({
      use_pulled_locations,
      insert_boxes,
      safe_mode,
    });

    success = true;

    return kmlDoc;
  } catch (e) {
    console.error('Could not download KML. Trying in safe mode.', e);
    logger.log('TO_KML', e);
    Sentry.captureException(e);
  }

  if (!success) {
    try {
      return this._to_kml({ insert_boxes, use_pulled_locations: false, safe_mode: true });
    } catch (e) {
      console.error('Could not download KML in safe mode.', e);
      logger.log('TO_KML_SAFE', e);
      Sentry.captureException(e);
    }
  }
}

function createFolder(parent: KMLElement, name: string, open: boolean = true, visibility: boolean = true) {
  const newFolder = KMLElement.subelement(parent, 'Folder');
  const folder_name = KMLElement.subelement(newFolder, 'name');
  folder_name.setText(name);
  const folder_open = KMLElement.subelement(newFolder, 'open');
  folder_open.setText(open ? '1' : '0');
  const folder_vis = KMLElement.subelement(newFolder, 'visibility');
  folder_vis.setText(visibility ? '1' : '0');

  return newFolder;
}

function createPlacemark(doc: KMLElement, name: string, lat: number, lon: number) {
  if (!Mission.arePullinPointCoordinatesValid(lat, lon)) {
    return;
  }

  const placemark = KMLElement.subelement(doc, 'Placemark');
  const placemark_vis = KMLElement.subelement(placemark, 'visibility');
  placemark_vis.setText('1');

  const nm = KMLElement.subelement(placemark, 'name');
  nm.setText(name);

  const point = KMLElement.subelement(placemark, 'Point');
  const coords = KMLElement.subelement(point, 'coordinates');
  coords.setText(`${lon},${lat},0`);

  const style = KMLElement.subelement(placemark, 'styleUrl');
  style.setText(`#${name}_location`);
}

/**
 *
 * @param this
 * @param param1
 * @returns
 *
 * This function is used to convert the mission to a KML document
 *
 * There are some subtle aspects to its design
 *
 * First, the loading mechanism in the app assumes there is some order to the serialized items
 * When the soil cores are loaded, it is assumed that centroids will exist to match to.
 * Therefore, the order of the items is important. (At least soil cores after centroids).
 *
 * This is the current order
 * - collected boundaries (if any)
 * - boundaries
 * - zones
 * - paths
 * - centroids
 * - cores
 */
export function _to_kml(
  this: Mission,
  { use_pulled_locations = false, insert_boxes = true, safe_mode = false } = {},
): KMLDocument | undefined {
  const startMs = performance.now();
  const kml = new KMLDocument();
  const doc = KMLElement.subelement(kml.getRoot(), 'Document');

  const doc_open = KMLElement.subelement(doc, 'open');
  doc_open.setText('1');

  const nm = KMLElement.subelement(doc, 'name');
  nm.setText(this.name);
  const descrip = KMLElement.subelement(doc, 'description');
  descrip.setText(this.description);

  createPlacemark(doc, KML_FEATURE_TYPE.PULLIN, this.pullin_lat, this.pullin_lon);
  createPlacemark(doc, KML_FEATURE_TYPE.PARKING_POINT, this.parking_point.lat, this.parking_point.lon);
  createPlacemark(doc, KML_FEATURE_TYPE.STARTING_POINT, this.starting_point.lat, this.starting_point.lon);
  createPlacemark(doc, KML_FEATURE_TYPE.END_POINT, this.end_point.lat, this.end_point.lon);

  const job = this.getJob();

  if (!job) {
    alertError('No job found for mission!');

    return;
  }

  doc.putExtendedData('rogo_version', getVersionNumber());
  doc.putExtendedData('job_id', this.job_id || '');
  doc.putExtendedData('attachment_id', job.attachment_id);
  doc.putExtendedData('test_package', job.test_package);
  doc.putExtendedData('event_id', job.event_id);
  doc.putExtendedData('client', job.client);
  doc.putExtendedData('grower', job.grower);
  doc.putExtendedData('grower_id', job.grower_id);
  doc.putExtendedData('farm', job.farm);
  doc.putExtendedData('field', job.field);
  doc.putExtendedData('billing_account', job.billing_account);
  doc.putExtendedData('sampling_company_name', job.sampling_company_name);
  doc.putExtendedData('sampling_company_id', job.sampling_company_id);
  doc.putExtendedData('response_email', job.response_email);
  doc.putExtendedData('lab_name', job.lab_name.replace(/"/g, ''));
  doc.putExtendedData('lab_code', job.lab_code);
  doc.putExtendedData('in_field_notes_ops', job.in_field_notes_ops);
  doc.putExtendedData('tilled_field_at_sampling', job.tilled_field_at_sampling);
  doc.putExtendedData('boundary_change_type', job.boundary_change_type);
  doc.putExtendedData('boundary_change_notes', job.boundary_change_notes);
  doc.putExtendedData('lab_address', job.lab_address);
  doc.putExtendedData('lab_primary_delivery', job.lab_primary_delivery);
  doc.putExtendedData('field_id', job.field_id);
  doc.putExtendedData('add_on_freq', job.add_on_freq);
  doc.putExtendedData('micros_list', job.micros_list.join(','));
  doc.putExtendedData('sample_order_type', job.sample_order_type);
  doc.putExtendedData('lab_submittal_id', job.lab_submittal_id);
  doc.putExtendedData('lab_qrs_required', job.lab_qrs_required);
  doc.putExtendedData('auto_renumber', job.auto_renumber);
  doc.putExtendedData('auto_fill_lab_instructions', job.auto_fill_lab_instructions);
  doc.putExtendedData('lab_instructions', job.lab_instructions);
  doc.putExtendedData('submitter_notified_id', job.submitter_notified_id);
  doc.putExtendedData('sites_type', job.sites_type);
  doc.putExtendedData('job_flags', JSON.stringify(job.job_flags));
  doc.putExtendedData('last_modified', this.last_modified);
  doc.putExtendedData('sample_date', this.sample_date);
  doc.putExtendedData('nav_checksum', this.nav_checksum);
  doc.putExtendedData('active', Boolean(this.getSession()));
  doc.putExtendedData('settings_changes', this.settings_changes);
  doc.putExtendedData('settings_start', this.settings_start);
  doc.putExtendedData('settings_end', this.settings_end);
  doc.putExtendedData('cores', this.cores);
  doc.putExtendedData('required_fields', job.required_fields);
  doc.putExtendedData('allowed_to_renumber', job.allowed_to_renumber);
  doc.putExtendedData('allowed_to_create', job.allowed_to_create);
  doc.putExtendedData('allowed_to_move', job.allowed_to_move);
  doc.putExtendedData('core_diameter', job.core_diameter);
  doc.putExtendedData('field_aligned_sample_path', job.field_aligned_sample_path);
  doc.putExtendedData('manual_hole_depth_measurement', job.manual_hole_depth_measurement);
  // doc.putExtendedData('manual_plunge_depth_measurement', job.manual_plunge_depth_measurement);
  doc.putExtendedData('manual_core_length_measurement', job.manual_core_length_measurement);
  doc.putExtendedData('manual_core_loss_measurement', job.manual_core_loss_measurement);
  doc.putExtendedData('use_original_sample_id', job.use_original_sample_id);
  doc.putExtendedData('plot_settings', job.plot_settings);
  doc.putExtendedData('plot_mission', job.plot_mission);
  doc.putExtendedData('disable_core_line_alteration', job.disable_core_line_alteration);
  doc.putExtendedData('entrance_interview_residue', job.entrance_interview_residue);
  doc.putExtendedData('entrance_interview_crop', job.entrance_interview_crop);
  doc.putExtendedData('sampling_tolerance_ft', job.sampling_tolerance_ft);
  doc.putExtendedData('sampling_type_special', job.sampling_type_special);
  doc.putExtendedData('implement_centerted_navigation', job.implement_centerted_navigation);
  doc.putExtendedData('zone_interleave', job.zone_interleave);
  doc.putExtendedData('deal_name', job.deal_name);
  doc.putExtendedData('call_prior_to_schedule', job.call_prior_to_schedule);
  doc.putExtendedData('enable_manual_drive_aid', job.enable_manual_drive_aid);
  doc.putExtendedData('auto_zoom_near_sample', job.auto_zoom_near_sample);
  doc.putExtendedData('lab_short_name', job.lab_short_name);
  doc.putExtendedData('open_time', job.open_time);
  doc.putExtendedData('offset_x', RobotArmOffsetX.get());
  doc.putExtendedData('offset_y', RobotArmOffsetY.get());
  doc.putExtendedData('backup_samples', JSON.stringify(this.backup_samples));

  if (insert_boxes) {
    // puts boxes as JSON strings into extended data
    SampleBox.toKml(this._instance_id, doc);

    // // puts boxes as XML elements in the file
    // const boxes_folder = KMLElement.element('SampleBoxes');
    // this.getAllSampleBoxes().forEach(box => {
    //   boxes_folder.append(box.to_kml());
    // });
    // doc.putExtendedData('SampleBoxes', boxes_folder);
  }

  SoilDumpEvent.toKml(this._instance_id, doc);

  SamplingSpec.toKml(this._instance_id, doc);

  // const specsFolder = KMLElement.element('SamplingSpecs');
  // this.getSamplingSpecs().forEach(spec => {
  //   const spec_kml = spec.to_kml();
  //   if (spec_kml) {
  //     specsFolder.append(spec_kml);
  //   }
  // });
  // doc.putExtendedData('SamplingSpecs', specsFolder);

  const target_folder = createFolder(doc, 'target', true, true);
  let collected_boundaries_folder: KMLElement | null = null;

  for (const collected_boundary of cleanSet(
    orderBy(
      this.getZones().filter((sel) => sel.zone_type === ZoneType.COLLECTED_FIELD),
      ['zone_name'],
      ['asc'],
    ),
  )) {
    const collected_boundary_kml = collected_boundary.to_kml();

    if (collected_boundary_kml && collected_boundary_kml.length > 0) {
      // I believe we add the folder inside the loop so that if there are none of these elements
      // we don't create an empty folder
      if (!collected_boundaries_folder) {
        collected_boundaries_folder = createFolder(target_folder, 'collected boundaries');
      }

      for (const bnd of collected_boundary_kml) {
        collected_boundaries_folder.append(bnd);
      }
    }
  }

  if (!safe_mode) {
    let boundaries_folder: KMLElement | null = null;

    for (const boundary of cleanSet(
      orderBy(
        this.getZones().filter(
          (sel) => sel.zone_type !== ZoneType.SAMPLE_ZONE && sel.zone_type !== ZoneType.COLLECTED_FIELD,
        ),
        ['zone_name'],
        ['asc'],
      ),
    )) {
      const boundary_kml = boundary.to_kml();

      if (boundary_kml && boundary_kml.length > 0) {
        if (!boundaries_folder) {
          boundaries_folder = createFolder(target_folder, 'boundaries');
        }

        for (const bnd of boundary_kml) {
          boundaries_folder.append(bnd);
        }
      }
    }

    let zones_folder: KMLElement | null = null;

    for (const zone of cleanSet(
      orderBy(
        this.getZones().filter((sel) => sel.zone_type === ZoneType.SAMPLE_ZONE),
        ['zone_name'],
        ['asc'],
      ),
    )) {
      const zone_kml = zone.to_kml();

      if (zone_kml && zone_kml.length > 0) {
        if (!zones_folder) {
          zones_folder = createFolder(target_folder, 'zones');
        }

        for (const zn of zone_kml) {
          zones_folder.append(zn);
        }
      }
    }

    let paths_folder: KMLElement | null = null;
    let path_placemark: KMLElement | null = null;
    let path_coords: KMLElement | null = null;

    for (const [i, waypoint] of cleanSet(orderBy(this.getWaypoints(), ['waypoint_number'], ['asc'])).entries()) {
      if (!path_placemark) {
        if (!paths_folder) {
          paths_folder = createFolder(target_folder, 'paths');
        }

        path_placemark = KMLElement.subelement(paths_folder, 'Placemark');
        const path_placemark_vis = KMLElement.subelement(path_placemark, 'visibility');
        path_placemark_vis.setText('1');
        const path_nm = KMLElement.subelement(path_placemark, 'name');
        path_nm.setText('path');
        const path_ls = KMLElement.subelement(path_placemark, 'LineString');
        path_coords = KMLElement.subelement(path_ls, 'coordinates');
        path_coords.setText('');
        const style = KMLElement.subelement(path_placemark, 'styleUrl');
        style.setText('#navigation_path');
      }

      if (path_coords) {
        path_coords.setText(`${path_coords.getText()}${i > 0 ? ' ' : ''}${waypoint.lon},${waypoint.lat},0`);
      }
    }

    // Pinned headings ride along as ExtendedData keyed by waypoint number
    // (only the pinned ones), so a mission reloaded from KML sends the robot
    // the same position.z it got before. MissionKmlLoader.loadNavigationPath
    // reads them back.
    const pinned: Record<string, number> = {};

    for (const waypoint of cleanSet(this.getWaypoints())) {
      if (waypoint.bearing_azimuth_deg !== undefined) {
        pinned[String(waypoint.waypoint_number)] = waypoint.bearing_azimuth_deg;
      }
    }

    if (path_placemark && Object.keys(pinned).length > 0) {
      path_placemark.putExtendedData(WAYPOINT_BEARINGS_KML_KEY, JSON.stringify(pinned));
    }
  }

  for (const layer of [SamplingTypes.REGULAR, SamplingTypes.ISNT_1, SamplingTypes.ISNT_2, SamplingTypes.CYST]) {
    let layer_folder: KMLElement | null = null;

    const _create_layer = () => {
      layer_folder = KMLElement.subelement(target_folder, 'Folder');
      const layer_folder_nm = KMLElement.subelement(layer_folder, 'name');

      if (layer === SamplingTypes.REGULAR) {
        layer_folder_nm.setText('Regular');
      } else if (layer === SamplingTypes.ISNT_1) {
        layer_folder_nm.setText('ISNT 1');
      } else if (layer === SamplingTypes.ISNT_2) {
        layer_folder_nm.setText('ISNT 2');
      } else if (layer === SamplingTypes.CYST) {
        layer_folder_nm.setText('Cyst');
      }

      const layer_folder_open = KMLElement.subelement(layer_folder, 'open');
      layer_folder_open.setText('1');

      return layer_folder;
    };

    let centroids_folder: KMLElement | null = null;
    const allSamplesOfLayerOrdered = this.getAllSamples().filter((sel) => sel.sample_type === layer);

    for (const sample of allSamplesOfLayerOrdered) {
      const sample_kml = sample.to_kml({ use_pulled_locations });

      if (sample_kml) {
        if (!centroids_folder) {
          if (!layer_folder) {
            layer_folder = _create_layer();
          }

          centroids_folder = createFolder(layer_folder, 'centroids');
        }

        centroids_folder.append(sample_kml);
      }
    }

    if (!safe_mode) {
      let cores_folder: KMLElement | null = null;
      const orderedCores: SoilCore[] = allSamplesOfLayerOrdered.reduce((acc: SoilCore[], sample: Sample) => {
        const sampleCores = orderBy(sample.getSoilCores(), 'core_number', ['asc']);
        acc.push(...sampleCores);

        return acc;
      }, []);

      for (const core of orderedCores) {
        const core_kml = core.to_kml(use_pulled_locations);

        if (core_kml) {
          if (!cores_folder) {
            if (!layer_folder) {
              layer_folder = _create_layer();
            }

            cores_folder = createFolder(layer_folder, 'cores');
          }

          cores_folder.append(core_kml);
        }
      }
    }
  }

  // build the style string
  const style_zones = cleanSet(this.getZones().filter((sel) => sel.zone_type === ZoneType.SAMPLE_ZONE)).map((sel) => ({
    id: `sample_zone_${sel.zone_name}`,
    color: sel.get_color(),
  }));
  doc.extendFromText(getStyleText(style_zones));

  return kml;
}

export async function downloadXag() {
  const mission = getCurrentMission();

  if (!mission) {
    alertError('No mission available');

    return;
  }

  const job = mission.getJob();

  if (!job) {
    alertError('No job available');

    return;
  }

  const xag = await mission.getXagKml();

  if (!xag) {
    alertError('Could not get xag');

    return;
  }

  const filename = `${job.field}_${job.grower}_${job.test_package}_${job.client}`.substring(0, 75) + `.kml`;

  const mimeType = 'application/vnd.google-earth.kml+xml';

  download(xag, filename, mimeType);
}

export async function unskipAllSkipped(this: Mission) {
  const samples = this.getSamples();

  samples.forEach((sample) => {
    sample.unskip();
  });

  await logger.log('mission_ops - unskipAllSkipped - calculate_path_restore_cores');
  await this.calculate_path_restore_cores([], null, false, true, true);
}

export async function skipUnsampled(this: Mission, change_type: SampleChangeType, change_reason: string) {
  // get all samples
  const samples = this.getSamples();

  for (const sample of samples) {
    if (sample.bag_id) {
      continue;
    }

    sample.change_type = change_type;
    sample.change_reason = change_reason;

    for (const wp of this.getWaypoints()) {
      if (wp.getCorePoint()?.getSoilCore()?.Sample_id === sample.instance_id) {
        wp.dispose();
      }
    }

    sample.resetPulled();
  }

  await logger.log('mission_ops - skipUnsampled - calculate_path_restore_cores');
  await this.calculate_path_restore_cores([], null, false, true, true);
}

export function getCoresTaken(this: Mission) {
  const coresText = this.cores;

  if (!coresText) {
    return [];
  }

  // parse the coresText as JSON
  const cores = JSON.parse(coresText) as TakenCore[];

  if (!cores) {
    return [];
  }

  return cores;
}

export function deleteExtraCoresForSamples(samples: Sample[]): number[] {
  const deleted: number[] = [];

  const soilCoresForDeletion = getAllExtraCores(samples);

  for (const core of soilCoresForDeletion) {
    // If the core doesn't need to be pulled, skip it.
    // It might be already pulled - we don't want to delete those.
    if (!core.isPending()) {
      continue;
    }

    deleted.push(core.instance_id);

    core.dispose();
  }

  return deleted;
}

export function getAllExtraCores(samples: Sample[]) {
  const maxDistance = ft_to_m(MAX_DISTANCE_BETWEEN_CLOSE_CORES_FT);

  let extraCores: SoilCore[] = [];

  for (const sample of samples) {
    const groupedCores = sample.groupCoresByProximity(maxDistance);
    groupedCores.forEach((group: SoilCore[]) => {
      // Use new way to determine if a core is an extra core.
      let extra = group.filter((c) => c.isExtraCore());

      if (extra.length === 0) {
        // Probably an old mission - use the old way.

        // Extra cores are all cores except for the last one which is the original core.
        extra = group.slice(0, group.length - 1);
      }

      extraCores = extraCores.concat(extra);
    });
  }

  return extraCores;
}

export async function reverse_path(this: Mission) {
  // First reverse all the waypoints
  const waypoints = orderBy(this.getWaypoints(), ['waypoint_number'], ['desc']);

  for (const [i, waypoint] of waypoints.entries()) {
    waypoint.waypoint_number = i + 1;
    const corePoint = waypoint.getCorePoint();
    const soilCore = corePoint?.getSoilCore();

    if (soilCore) {
      soilCore.waypoint_index = waypoint.waypoint_number;
    }
  }

  for (const [i, sample] of orderBy(this.getSamples(), ['order'], ['desc']).entries()) {
    sample.order = i;
  }

  // Every planned heading was for the forward direction of its hop.
  clearAllBearings(this);

  // recalculate checksum
  this.to_ros_msg();
  dispatchMissionUpdated();
  // waypoint_index on cores was reversed → CorePointsLayer needs to rebuild.
  dispatchCoresNeedUpdate();
}

export function calculate_path_distance(mission: Mission) {
  const waypoints = orderBy(cleanSet(mission.getWaypoints()), ['waypoint_number'], ['asc']);

  let firstSampleIdx = -1;
  let lastSampleIdx = -1;

  for (let i = 0; i < waypoints.length; i++) {
    if (waypoints[i].getRelatedSample()) {
      if (firstSampleIdx === -1) {
        firstSampleIdx = i;
      }

      lastSampleIdx = i;
    }
  }

  if (firstSampleIdx < 0 || firstSampleIdx === lastSampleIdx) {
    return 0;
  }

  let total = 0;

  for (let i = firstSampleIdx; i < lastSampleIdx; i++) {
    total += calcLineDistance(waypoints[i], waypoints[i + 1]);
  }

  return total;
}

export async function calculate_path_restore_cores(
  this: Mission,
  reordered_points_of_interest: Sample[] | SoilCore[] = [],
  initial_prev_point: Coordinate | null = null,
  matchCoresByPosition: boolean = false,
  keepIntermediateWaypoints: boolean = true,
  preserveExtras: boolean = false,
) {
  await logger.log('calculate_path_restore_cores - start', {
    matchCoresByPosition,
    keepIntermediateWaypoints,
    pointsOfInterestLength: reordered_points_of_interest.length,
  });

  const backupPulledCores: Record<string, string> = {};

  // save cores temporarily

  const samples = this.getSamples();

  for (const sample of samples) {
    backupPulledCores[sample.sample_id] = JSON.stringify(sample.getPulledSoilCores());
  }

  let reorderItems: Sample[] | SoilCore[] | NavigationPoint[] = reordered_points_of_interest;

  if (reorderItems.length === 0) {
    if (this.is_zone_mission()) {
      // For zone missions, preserve path order from existing waypoints
      // and append any pending cores not in waypoints (e.g., from unskipped samples).
      // (unskipped preserved waypoint_index - use it for ordering)
      const waypoints = [...this.getWaypoints()].sort((a, b) => a.waypoint_number - b.waypoint_number);
      const coresFromWaypoints = waypoints
        .map((wp) => wp.getCorePoint()?.getSoilCore())
        .filter((core): core is SoilCore => !!core);

      const allPendingCores = this.getPendingCores();
      const waypointCoreIds = new Set(coresFromWaypoints.map((c) => c.instance_id));
      const missingCores = allPendingCores
        .filter((c) => !waypointCoreIds.has(c.instance_id))
        .sort(
          preserveExtras
            ? (a, b) => (a.waypoint_index || Infinity) - (b.waypoint_index || Infinity)
            : SoilCore.sortBySampleOrderAndCoreNumber,
        );

      console.log('missing cores', missingCores);

      reorderItems = [...coresFromWaypoints, ...missingCores];
    } else {
      const waypoints = this.getWaypoints();

      if (waypoints.length) {
        reorderItems = waypoints.map((waypoint) => waypoint.getCorePoint()?.getSoilCore()).filter((core) => !!core);
      }
    }
  }

  let intermediateWaypointsMap: IntermediateWaypointsMap = {};

  if (keepIntermediateWaypoints) {
    intermediateWaypointsMap = this.removeWaypointsAndGenerateIntermediateWaypointsMap();
  } else {
    this.removeWaypoints();
  }

  try {
    await this.calculate_path(reorderItems, null, intermediateWaypointsMap, preserveExtras);
  } catch (e) {
    if (e && typeof e === 'object' && 'message' in e) {
      alertError(`Error occurred: ${(e as { message: string }).message || 'no details'}`);
    } else {
      alertError(`Error occurred: ${String(e) || 'no details'}`);
    }
  }

  // Serg 01-12-2025: We no longer need to restore cores because we preserve them during path calculation.

  // for (const sample of samples) {
  //   // so these aren't *really* SoilCoreClass objects in that they aren't instances of the class
  //   // but they are objects that have the same properties as the class
  //   const sampleBackupCores: SoilCoreClass[] = JSON.parse(backupPulledCores[sample.sample_id]);
  //   for (const soilCore of sample.getSoilCores()) {
  //     let backupCore: SoilCoreClass | undefined;
  //     if (matchCoresByPosition) {
  //       backupCore = sampleBackupCores.find(
  //         (backupCore) => backupCore.lat === soilCore.lat && backupCore.lon === soilCore.lon,
  //       );
  //     } else {
  //       backupCore = sampleBackupCores.find((backupCore) => backupCore.core_number === soilCore.core_number);
  //     }

  //     if (backupCore) {
  //       soilCore.restorePulledInfo(backupCore);
  //     } else if (!sample.skipped_or_deleted && sample.pulled_at) {
  //       alertError(`Soil core ${soilCore.name} was not restored - something went wrong!`);
  //     }
  //   }
  // }
}

// Apply a smart-path tour to a grid mission whose cores already exist.
//
// Pathfinder is given the first + last core of each sample (pair-of-cores
// model) and returns a tour through those endpoints plus inter-sample
// nav-grid-points. This function rebuilds the mission's waypoint sequence
// in pathfinder's order WITHOUT regenerating cores. That preserves both
// the core-line angle and the entry/exit direction pathfinder optimised
// against — re-running the spec layout (calculate_path_restore_cores)
// would re-pick the line orientation based on prev/next neighbours and
// could rotate or flip the line, invalidating pathfinder's cost matrix.
//
// Flow per nav-visit pair:
//   1. Find pathfinder's two nav-visits for the sample.
//   2. Match them to existing cores by name; the entry core's core_number
//      vs the exit core's core_number gives the traversal direction.
//   3. Emit waypoints + CorePoints for ALL the sample's pending cores in
//      that direction (between core 1 and core N inclusive).
// Inter-sample nav-grid-points / start / end are emitted as raw waypoints.
export async function applySmartPathTourToExistingCores(this: Mission, navigationPoints: NavigationPoint[]) {
  await logger.log('applySmartPathTourToExistingCores - start', {
    pointsCount: navigationPoints.length,
  });

  this.removeWaypoints();

  const visitedSampleIds = new Set<string>();
  const samples = this.getSamples();
  let nextSampleOrder = 1;

  for (let i = 0; i < navigationPoints.length; i++) {
    const np = navigationPoints[i];

    if (np.type !== 'nav-visit' || !np.sampleId) {
      this.addWaypoint(np.lat, np.lon, np.bearingAzimuthDeg);
      continue;
    }

    const sampleId = np.sampleId;

    if (visitedSampleIds.has(sampleId)) {
      // Second pair endpoint — handled when we hit the first.
      continue;
    }

    visitedSampleIds.add(sampleId);

    const sample = samples.find((s) => s.sample_id === sampleId);

    if (!sample) {
      continue;
    }

    if (!sample.pulled_at) {
      sample.order = nextSampleOrder++;
    }

    const pendingCores = cleanSet(sample.getSoilCores()).filter((c) => c.isPending());

    if (pendingCores.length === 0) {
      continue;
    }

    const sortedCores = [...pendingCores].sort((a, b) => a.core_number - b.core_number);
    let orderedCores = sortedCores;

    const pairSibling = findPairSibling(navigationPoints, i, sampleId);

    if (pairSibling) {
      const entryCore = pendingCores.find((c) => c.name === np.id);
      const exitCore = pendingCores.find((c) => c.name === pairSibling.id);

      if (entryCore && exitCore && entryCore.core_number > exitCore.core_number) {
        orderedCores = [...sortedCores].reverse();
      }
    }

    let waypointNumber = cleanSet(this.getWaypoints()).length + 1;

    for (const core of orderedCores) {
      const waypoint = Waypoint.create();
      waypoint.Mission_id = this.instance_id!;
      waypoint.waypoint_number = waypointNumber++;
      waypoint.lat = core.lat;
      waypoint.lon = core.lon;
      // Keep whatever bearing pathfinder put on the navigation point that
      // produced this core (entry core ← np, exit core ← its pair sibling;
      // the cores in between came from no point). Pathfinder never pins grid
      // cores today, so these stay undefined — the plumbing is the same as
      // the zone applier's so a future pin would flow through unchanged.
      const producer = [np, pairSibling].find((p) => p?.id === core.name);
      waypoint.bearing_azimuth_deg = producer?.bearingAzimuthDeg;
      const corePoint = CorePoint.create();
      corePoint.Waypoint_id = waypoint.instance_id!;
      corePoint.SoilCore_id = core.instance_id!;
    }
  }

  this.path_distance = calculate_path_distance(this);
  alertInfo(`Path is ${Math.round(this.path_distance / 100 / KM_PER_MILE) / 10} miles long`);
  this.to_ros_msg();

  dispatchMissionUpdated();
  dispatchCoresNeedUpdate();
}

function findPairSibling(
  navigationPoints: NavigationPoint[],
  fromIndex: number,
  sampleId: string,
): NavigationPoint | undefined {
  for (let j = fromIndex + 1; j < navigationPoints.length; j++) {
    const np = navigationPoints[j];

    if (np.type === 'nav-visit' && np.sampleId === sampleId) {
      return np;
    }
  }

  return undefined;
}

// Per-sample distribution of "how many extras each original gets".
// Splits (spec.cores - originalCount) evenly across the originals;
// leftover (remainder) gets distributed one-per-original to the first
// few originals processed (so the first |remainder| originals each get
// one extra more than the rest).
export type SampleExtrasDistribution = {
  coresNumberToAddToAllOriginalCores: number;
  remainder: number;
};

export function computeExtrasDistribution(
  samples: Sample[],
  specCores: number,
): { [sampleId: string]: SampleExtrasDistribution } {
  const result: { [sampleId: string]: SampleExtrasDistribution } = {};

  for (const sample of samples) {
    const originalCount = Math.max(sample.getSoilCores().length, 1);
    result[sample.sample_id] = {
      coresNumberToAddToAllOriginalCores: Math.max(0, Math.floor((specCores - originalCount) / originalCount)),
      remainder: Math.max(0, (specCores - originalCount) % originalCount),
    };
  }

  return result;
}

// Apply a smart-path tour to a zone mission whose extras have been
// stripped (deleteExtraCoresForSamples ran in SmartPathBuilder). This
// rebuilds the waypoint sequence in pathfinder's order and regenerates
// extras using a HYBRID placement:
//
//   - First original of each sample (prev = different sample): extras
//     AFTER on the outgoing line. The first original's heading via
//     `dep-core-diff-sample` becomes bearing(O1 → first-after-extra) =
//     bearing(O1 → O2) — i.e. forward direction. Matches pathfinder's
//     K-bin pick (which also lands near forward to minimise arc cost).
//
//   - Subsequent originals in the same sample (prev = same-sample
//     extra): extras BEFORE on the entry line. `arr-same-sample` then
//     locks the original + every extra to the entry-line direction, so
//     within-cluster legs are 0.55 m close-path-straight (correctly so).
//     The transition arc between incoming and outgoing direction lives
//     on the long inter-cluster leg `O_k → first-before-extra-of-O_(k+1)`
//     where pairDistance is the full sample-to-sample gap, well above
//     close-path's 1 m unconditional threshold — the curve at O_k stays
//     visible.
//
// Pure AFTER would put a near-180° heading change in 0.55 m at every
// middle original, which close-path renders as a straight line and
// visually "eats" the curve. Pure BEFORE would shift each first-of-
// sample original's heading to the entry line, mismatching pathfinder's
// forward-direction K-bin pick.
//
// Why we don't move existing originals: deleteExtraCoresForSamples uses
// SoilCore.isExtraCore() (source === 'Rogo Extra Core'), so only extras
// are deleted. Originals stay put and the cluster centre doesn't drift
// across smart-path runs.
export async function applyZoneSmartPathTourToExistingCores(this: Mission, navigationPoints: NavigationPoint[]) {
  await logger.log('applyZoneSmartPathTourToExistingCores - start', {
    pointsCount: navigationPoints.length,
  });

  this.removeWaypoints();

  const spec = this.getPrimarySpec();

  if (!spec) {
    alertError('Cannot apply smart-path tour without a sampling spec');

    return;
  }

  const coresInfo = computeExtrasDistribution(this.getSamples(), spec.cores);
  const STEP_M = ft_to_m(CORE_STOP_MAX_DISTANCE_FT);

  // Track which samples we've already seen so we can branch placement
  // strategy for the first vs middle originals of each sample.
  const sampleFirstSeen = new Set<string>();

  // `bearingAzimuthDeg`: the heading pathfinder pinned on the original (the
  // robot's lon/lat azimuth). When present the extras lie on that line —
  // ahead of the original (forward placement) or behind it (reverseOrder,
  // the entry side) — so the robot, which arrives at the original along the
  // pinned heading, runs straight through the whole cluster. Without a
  // bearing the line towards the reference waypoint is used as before. The
  // reference still caps the spacing so the extras never overshoot it.
  const placeExtras = (
    sample: Sample,
    origLat: number,
    origLon: number,
    refLat: number,
    refLon: number,
    extrasCount: number,
    startingWaypointNumber: number,
    reverseOrder: boolean = false,
    bearingAzimuthDeg?: number,
  ): number => {
    if (extrasCount === 0) {
      return startingWaypointNumber;
    }

    const origUtm = fromLatLon(origLat, origLon);
    const refUtm = fromLatLon(refLat, refLon);
    const dx = refUtm.easting - origUtm.easting;
    const dy = refUtm.northing - origUtm.northing;
    const refDist = Math.sqrt(dx * dx + dy * dy);

    if (refDist <= 0) {
      return startingWaypointNumber;
    }

    const maxSafeSpacing = refDist / (extrasCount + 1);
    const spacing = Math.min(STEP_M, maxSafeSpacing);
    const ux = dx / refDist;
    const uy = dy / refDist;

    // Position of the j-th extra, `spacing * j` metres from the original.
    const extraLatLon = (j: number): { latitude: number; longitude: number } => {
      if (bearingAzimuthDeg === undefined) {
        const e = origUtm.easting + ux * spacing * j;
        const n = origUtm.northing + uy * spacing * j;

        return toLatLon(e, n, origUtm.zoneNum, origUtm.zoneLetter);
      }

      // The azimuth is a direction in degree space: a step of (cos, sin)
      // degrees, whose metric length depends on the latitude. Scale it so
      // consecutive extras are `spacing` metres apart along that line.
      const a = (bearingAzimuthDeg * Math.PI) / 180;
      const side = reverseOrder ? -1 : 1;
      const stepLon = Math.cos(a);
      const stepLat = Math.sin(a);
      const metresPerStep = Math.hypot(stepLon * metresPerDegreeLon(origLat), stepLat * metresPerDegreeLat(origLat));
      const k = (side * spacing * j) / metresPerStep;

      return { latitude: origLat + k * stepLat, longitude: origLon + k * stepLon };
    };

    let wpNum = startingWaypointNumber;

    // Iteration direction controls which extra gets the smallest WP number,
    // i.e. which one the tour visits FIRST. Forward (j=1..N) places the
    // closest-to-origin extra first (correct for first-of-sample where
    // origin is the entry point and the tour walks origin → ref).
    // Reverse (j=N..1) places the farthest-from-origin extra first (correct
    // for middle-of-sample where origin is the destination and the tour walks
    // ref → origin: visiting closest-to-ref first avoids a U-turn at the
    // last extra → original transition).
    const jStart = reverseOrder ? extrasCount : 1;
    const jEnd = reverseOrder ? 1 : extrasCount;
    const jStep = reverseOrder ? -1 : 1;

    for (let j = jStart; reverseOrder ? j >= jEnd : j <= jEnd; j += jStep) {
      const latLon = extraLatLon(j);
      // The extras lie ON the cluster's pinned line, so they carry the same
      // bearing as the original that produced them: the robot holds that
      // heading through the whole cluster instead of deriving one from a
      // neighbour ~0.6 m away (which is how a sharp, unplanned turn appears at
      // the original→extra→original hops). No pin on the original → no pin here.
      create_new_core_with_waypoint_and_core_point(
        [latLon.latitude, latLon.longitude],
        this.instance_id!,
        sample,
        wpNum,
        'Rogo Extra Core',
        undefined,
        bearingAzimuthDeg,
      );
      wpNum++;
    }

    return wpNum;
  };

  // `np` is the navigation point pathfinder emitted for this original; it
  // carries the planned bearing when pathfinder pinned the core's heading
  // (zone cores that are not a c2c chord arrival), nothing otherwise.
  const emitOriginalWaypoint = (original: SoilCore, waypointNumber: number, np: NavigationPoint): number => {
    const origWp = Waypoint.create();
    origWp.Mission_id = this.instance_id!;
    origWp.waypoint_number = waypointNumber;
    origWp.lat = original.lat;
    origWp.lon = original.lon;
    origWp.bearing_azimuth_deg = np.bearingAzimuthDeg;
    const origCp = CorePoint.create();
    origCp.Waypoint_id = origWp.instance_id!;
    origCp.SoilCore_id = original.instance_id!;
    original.waypoint_index = waypointNumber;

    return waypointNumber + 1;
  };

  for (let i = 0; i < navigationPoints.length; i++) {
    const np = navigationPoints[i];

    if (np.type !== 'nav-visit' || !np.sampleId) {
      this.addWaypoint(np.lat, np.lon, np.bearingAzimuthDeg);
      continue;
    }

    const sample = this.getSamples().find((s) => s.sample_id === np.sampleId);

    if (!sample) {
      continue;
    }

    const original = cleanSet(sample.getSoilCores()).find((c) => c.name === np.id && c.isPending());

    if (!original) {
      this.addWaypoint(np.lat, np.lon, np.bearingAzimuthDeg);
      continue;
    }

    // Compute extras count for THIS original (mutates info.remainder).
    const info = coresInfo[np.sampleId];
    const oneMore = info && info.remainder > 0 ? 1 : 0;
    const extrasCount = (info?.coresNumberToAddToAllOriginalCores ?? 0) + oneMore;

    if (info && info.remainder > 0) {
      info.remainder--;
    }

    const isFirstOfSample = !sampleFirstSeen.has(np.sampleId);
    sampleFirstSeen.add(np.sampleId);

    let waypointNumber = cleanSet(this.getWaypoints()).length + 1;

    if (isFirstOfSample) {
      // First-of-sample: emit original FIRST, then extras AFTER on the
      // outgoing line (toward the next tour point).
      waypointNumber = emitOriginalWaypoint(original, waypointNumber, np);

      const nextRef = navigationPoints[i + 1];

      if (nextRef) {
        waypointNumber = placeExtras(
          sample,
          original.lat,
          original.lon,
          nextRef.lat,
          nextRef.lon,
          extrasCount,
          waypointNumber,
          false,
          np.bearingAzimuthDeg,
        );
      }
    } else {
      // Middle original: emit extras BEFORE on the entry line (toward
      // the previous waypoint), then the original.
      const currentWaypoints = orderBy(cleanSet(this.getWaypoints()), ['waypoint_number'], ['asc']);
      const prevWp = currentWaypoints[currentWaypoints.length - 1];

      if (prevWp && extrasCount > 0) {
        // For BEFORE placement we need the extras to land BETWEEN prev
        // and original. placeExtras puts them at distance j*spacing from
        // origin along bearing toward ref — passing prev as ref gives us
        // the right direction; the extras will sit between original and
        // prev with the closest extra at distance `spacing` from original.
        //
        // reverseOrder=true so the FARTHEST-from-original extra (closest
        // to prev) gets the smallest WP number. The tour then walks prev
        // → far → close → original monotonically. Without this the last
        // extra-before-original lands 0.61 m from original on the prev
        // side, the tour visits it AFTER a closer extra at 1.22 m, and
        // the resulting 0.61 m backtrack with a 180° heading flip forces
        // a 36.57 m RLR Dubin's loop at every middle-original cluster.
        waypointNumber = placeExtras(
          sample,
          original.lat,
          original.lon,
          prevWp.lat,
          prevWp.lon,
          extrasCount,
          waypointNumber,
          true,
          np.bearingAzimuthDeg,
        );
      }

      waypointNumber = emitOriginalWaypoint(original, waypointNumber, np);
    }
  }

  // Renumber cores + assign sample.order in tour order. We do it AFTER the
  // walk (rather than in buildSmartPath before this function) because the
  // walk relies on each core's current `name` (= sample_id + core_number)
  // to look up the original — running setSampleAndCoreOrder beforehand
  // would mutate names mid-loop. Skip for interleave missions, matching
  // the previous behaviour.
  if (!this.is_zone_interleave_mission()) {
    const orderedCores = orderBy(cleanSet(this.getWaypoints()), ['waypoint_number'], ['asc'])
      .map((wp) => wp.getCorePoint()?.getSoilCore())
      .filter((c): c is SoilCore => !!c);
    setSampleAndCoreOrder(orderedCores);
  }

  this.path_distance = calculate_path_distance(this);
  alertInfo(`Path is ${Math.round(this.path_distance / 100 / KM_PER_MILE) / 10} miles long`);
  this.to_ros_msg();

  dispatchMissionUpdated();
  dispatchCoresNeedUpdate();
}

export async function calculate_path(
  this: Mission,
  reorderedPointsOfInterest: Sample[] | SoilCore[] = [],
  initialPrevPoint: Coordinate | null = null,
  intermediateWaypointsMap: IntermediateWaypointsMap = {},
  preserveExtras: boolean = false,
) {
  await logger.log('calculate_path - start');

  const spec = this.getPrimarySpec();

  if (!spec) {
    alertError('Cannot calculate path without a sampling spec');

    return;
  }

  if (this.is_zone_mission()) {
    const zonePathGenerator = new ZonePathGenerator(
      this,
      spec,
      reorderedPointsOfInterest as SoilCore[],
      intermediateWaypointsMap,
      preserveExtras,
    );

    await zonePathGenerator.generate();
  } else {
    const gridPathGenerator = new GridPathGenerator(this, spec, initialPrevPoint, intermediateWaypointsMap);

    gridPathGenerator.generate();
  }

  this.path_distance = calculate_path_distance(this);

  // show distance in miles
  alertInfo(`Path is ${Math.round(this.path_distance / 100 / KM_PER_MILE) / 10} miles long`);

  // call 'to_ros_msg' to re-calculate the nav checksum
  this.to_ros_msg();

  dispatchMissionUpdated();
  // smart_path regenerated waypoints and may have moved cores /
  // changed their waypoint_index — CorePointsLayer needs to rebuild.
  dispatchCoresNeedUpdate();
}

// Generate the lab check-in CSV(s) with the canonical shared-ts generator so
// the in-app download matches the cloud-generated CSVs exactly. Builds a
// shared-ts Mission from this mission's KML (the same artifact the cloud
// ingests), then supplies job/deal/company DTOs fetched fresh from CRM.
// Returns one MissionCSV per spec/deal (usually one).
export async function generate_lab_csvs(this: Mission): Promise<MissionCSV[]> {
  const kml = this.to_kml({ insert_boxes: true });

  if (!kml) {
    alertError('Could not generate CSV: mission KML unavailable');

    return [];
  }

  try {
    const sharedMission = await new MissionLoader({ data: kml.toString(), filename: '.kml' }).parse();

    // Airtable job: source of rogoJobId/apiId/sample-date/event-id/etc. Mirrors
    // the proven single-record lookup in update_job_details_with_airtable
    // (crmBase.jobs.get(id) returns one record, not an array).
    const atJob = this.job_id ? await crmBase.jobs.get(this.job_id) : undefined;

    // Per-spec deals (referenced by the shared mission's specs) + the job's
    // primary deal as the jobDeal fallback. Matches the cloud's
    // getDealIdForSpec resolution; fetch shape mirrors update_job_with_deal.
    const primaryDealId = atJob?.deal?.id;
    const dealIds = Array.from(new Set([primaryDealId, ...sharedMission.dealIds].filter((id): id is string => !!id)));
    const atDeals = (
      await Promise.all(
        dealIds.map((id) => crmBase.deals.get({ view: 'App View - Active Deals', formula: `RECORD_ID() = '${id}'` })),
      )
    )
      .map((deals) => deals?.[0])
      .filter((deal): deal is AtDeal => !!deal);

    // Company: only externalSampleReferenceIdColumn is consumed (validation).
    // Mirrors update_job_with_company_details.
    const companyId = atJob?.clientOriginalName?.[0];
    const company = companyId
      ? (
          await crmBase.companies.get({
            view: 'Active Companies - Deals OR Customer Status Current',
            formula: Company.f.id.equals(companyId),
          })
        )?.[0]
      : undefined;

    const deals = atDeals.map(toIDeal);
    const jobDeal = deals.find((d) => d.id === primaryDealId) ?? deals[0] ?? toIDeal(undefined);

    return sharedMission.generateLabCSVs(toIJob(atJob), jobDeal, deals, toICompany(company));
  } catch (e) {
    console.error('generate_lab_csvs error', e);
    alertError(`Could not generate CSV: ${e instanceof Error ? e.message : String(e)}`);

    return [];
  }
}

export async function to_shapefile(this: Mission, pts: boolean, bnds: boolean) {
  // generate geoJSON
  const [geoJSON, options] = this._to_shapefile(pts, bnds);

  try {
    // return zip contents
    const result: Blob = await shpZip<'blob'>(geoJSON as FeatureCollection<any, any>, options);

    return result;
  } catch (e) {
    console.error('to_shapefile error: ', e);
  }
}

export function _to_shapefile(this: Mission, pts: boolean, bnds: boolean) {
  const job = this.getJob();

  if (!job) {
    throw new Error('Mission has no job');
  }

  const spec = this.getPrimarySpec();

  if (!spec) {
    throw new Error('Mission has no spec');
  }

  const samples = cleanSet(
    orderBy(flatten(this.getSampleSites().map((site) => site.getSamples())), ['order'], ['asc']),
  );

  const geoJSON: FeatureCollection = {
    type: 'FeatureCollection',
    features: [],
  };

  if (pts) {
    // sample sites
    for (const [i, sample] of samples.entries()) {
      const sample_centroid = sample.getSampleSite().getSampleCentroid();
      const sample_tests: string[] = [];
      const feature: Feature<Point> = {
        type: 'Feature',
        properties: {},
        id: sample.sample_id,
        geometry: { type: 'Point', coordinates: [] },
      };

      for (const t of job.test_package.split('+')) {
        if (!t.endsWith('*')) {
          sample_tests.push(t);
        } else if (isSampleMicro(true, job.add_on_freq, job.micros_list, i, sample.getOriginalSampleId())) {
          sample_tests.push(t.slice(0, -1));
        }
      }

      if (sample_centroid) {
        feature.geometry.coordinates = [sample_centroid.lon, sample_centroid.lat];
      } else {
        feature.geometry.coordinates = [];
      }

      feature.properties = {
        'spl brcd': sample.bag_id,
        'spl id': sample.sample_id,
        'no spl': samples.length.toString(),
        'test pkg': sample_tests.join('+'),
        'spl ev id': job.event_id,
        date: this.sample_date > 0.0 ? new Date(this.sample_date * 1000).toISOString() : '',
        client: job.client,
        grower: job.grower,
        farm: job.farm,
        field: job.field,
        'st dpth': spec.start_depth.toString(),
        'end dpth': spec.end_depth.toString(),
        'b acct no': job.billing_account,
        'co name': job.sampling_company_name,
        'co ID': job.sampling_company_id,
        'Rogo id': job.job_id,
        email: job.response_email,
        'fld ID': job.field_id,
        'o spl id': sample.getSampleSite()?.original_sample_id || '',
        'spl ref': sample.getSampleSite()?.external_reference_id || '',
      };

      geoJSON.features.push(feature);
    }
  }

  if (bnds) {
    // zones
    const collected_zones = cleanSet(
      orderBy(
        this.getZones().filter((sel) => sel.zone_type === ZoneType.COLLECTED_FIELD),
        ['zone_name'],
        ['asc'],
      ),
    );
    const field_zones = cleanSet(
      orderBy(
        this.getZones().filter((sel) => sel.zone_type === ZoneType.FIELD),
        ['zone_name'],
        ['asc'],
      ),
    );
    let zones: Zone[];

    // default to collected zones otherwise fall back to field zones
    if (collected_zones.length > 0) {
      zones = collected_zones;
    } else {
      zones = field_zones;
    }

    zones = zones.concat(this.getZonesOfTypeSampleZone());

    for (const zone of zones) {
      // outer polys
      const outer_boundaries = cleanSet(orderBy(zone.getOuterZoneBoundarys(), ['poly_id'], ['asc']));

      for (const outer_boundary of outer_boundaries) {
        const feature: Feature<Polygon> = {
          type: 'Feature',
          properties: {
            field: job.field,
            farm: job.farm,
            grower: job.grower,
            id: 'ob' + outer_boundary.poly_id,
          },
          geometry: { type: 'Polygon', coordinates: [] },
        };

        const outer_coordinates: Coordinate[] = [];

        for (const [, outerCoords] of outer_boundary.coordinates.entries()) {
          outer_coordinates.push([outerCoords[1], outerCoords[0]]);
        }

        feature.geometry.coordinates.push(outer_coordinates);

        const inner_boundaries = cleanSet(
          orderBy(
            zone.getInnerZoneBoundarys().filter((sel) => sel.poly_id === outer_boundary.poly_id),
            ['boundary_id'],
            ['asc'],
          ),
        );

        // inner polys
        for (const inner_boundary of inner_boundaries) {
          const inner_coordinates: Coordinate[] = [];

          for (const [, innerCoords] of inner_boundary.coordinates.entries()) {
            inner_coordinates.push([innerCoords[1], innerCoords[0]]);
          }

          feature.geometry.coordinates.push(inner_coordinates);
        }

        geoJSON.features.push(feature);
      }
    }
  }

  // name zipped folder and files
  const options: DownloadOptions & ZipOptions = {
    folder: this.name,
    types: {
      point: `${this.name}_pts`,
      polygon: `${this.name}_bnd`,
    },
    compression: 'DEFLATE',
    outputType: 'blob',
  };

  return [geoJSON, options] as const;
}

export function to_pdf(this: Mission, logo: string | ArrayBuffer, pdfSampleOrder: string, config: PRINTER_CONFIG) {
  // const mission = [this];
  return {
    info: { title: `${this.job_id}.pdf` },
    // @ts-ignore
    content: [genPdfCheckinOld([this], logo, pdfSampleOrder, null, this.getAllSamples(), config)],
    pageSize: config.pageSize,
    defaultStyle: config.defaultStyle,
    pageMargins: config.lab.margin,
  } as TDocumentDefinitions;
}

export async function create_zone_cores(this: Mission, coords: Coordinate[]) {
  const primarySpec = this.getPrimarySpec();

  if (!primarySpec) {
    throw new Error('Creating zone cores - primary sampling spec not found');
  }

  let order = 1;

  for (const [lat, lon] of coords) {
    // get sample zone that this point is in
    let sample_site: SampleSite | undefined = undefined;
    const sampleSites = this.getSampleSites();
    const sampleZones = sampleSites.map((site) => site.getSampleZone());
    const zones = cleanSet(sampleZones.map((szone) => szone?.getZone()));

    for (const zone of zones) {
      if (!zone) {
        continue;
      }

      if (!zone.hasValidBoundaries()) {
        const errorMessage = `Zone ${zone.zone_name} has invalid boundaries`;
        alertError(errorMessage);

        throw new Error(errorMessage);
      }

      if (zone.contains(lat, lon)) {
        sample_site = zone.getSampleZone()?.getSampleSite();
        break;
      }
    }

    if (!sample_site) {
      continue;
    }

    let sample = cleanSet(sample_site.getSamples())[0];

    if (!sample) {
      sample = Sample.createSample(
        this,
        sample_site,
        primarySpec,
        sample_site.original_sample_id,
        SamplingTypes.REGULAR,
      );
    }

    // Sergei, 2025-09-13: this only works if a user draws all cores at once (wih a single core line).
    // Otherwise, the order is messed up and can be duplicate with other samples.
    // Leaving it as is for now since it's been here forever, but consider refactoring.
    sample.order = order++; // make the sample order the same as the cores path

    const core = SoilCore.create();
    core.Sample_id = sample.instance_id;
    core.core_number = Math.max(...cleanSet(sample.getSoilCores()).map((sel) => sel.core_number)) + 1;
    core.lat = lat;
    core.lon = lon;
    core.source = 'Rogo Map Maker';
  }
}

function isPolygonOrMultiPolygon(geometry): geometry is Polygon | MultiPolygon {
  return geometry.type === 'Polygon' || geometry.type === 'MultiPolygon';
}

function isPolygon(geometry): geometry is Polygon {
  return geometry.type === 'Polygon';
}

function isPoint(geometry): geometry is Point {
  return geometry.type === 'Point';
}

export async function generate_from_shapes(
  missionWithJob: Mission,
  shpfiles: RogoShapefile[],
  confirmColumn: (item: ConfirmColumn) => Promise<string>,
): Promise<[Mission, string[]]> {
  const error_messages: string[] = [];

  try {
    // load the shapefiles in the Job checker
    const shapeData = shpfiles.map((shpfile) => {
      return { filename: shpfile.name, data: shpfile.data };
    });

    const sharedMissionLoader = new MissionLoader(shapeData);
    const sharedMission = await sharedMissionLoader.parse();
    const sharedMissionChecker = new MissionChecker(sharedMission);
    const errors = sharedMissionChecker.validateSubmittable();

    errors.forEach((err) => {
      if (err && typeof err === 'object' && 'rogo' in err && 'message' in err) {
        alertWarn(err.message);
      } else {
        alertWarn(JSON.stringify(err));
      }
    });
  } catch (err) {
    if (!!err && typeof err === 'object' && 'rogo' in err && 'message' in err) {
      alertWarn(`Found mission problem: ${err.message}`);
    } else {
      alertWarn(`Unknown error hit during validation: ${JSON.stringify(err)}`);
    }
  }

  const parsedShapeFiles = await ShapefileParserService.getInstance().parseShapefiles(shpfiles);

  // analyze each feature set
  for (const shpfile of parsedShapeFiles) {
    const shpDataArray = Array.isArray(shpfile.data) ? shpfile.data : [shpfile.data];

    if (shpfile.mode === 'bnd') {
      await processBoundaryFile(shpDataArray, missionWithJob, error_messages);
    } else if (shpfile.mode === 'pts') {
      await processPointsOrZonesFile(shpDataArray, confirmColumn, shpfile, error_messages, missionWithJob);
    } else {
      console.error(`Unknown mode: ${shpfile.mode}`);
      error_messages.push(`Unknown mode: ${shpfile.mode}`);
    }
  }

  if (missionWithJob.is_zone_mission()) {
    // Geomatch points with zones
    missionWithJob.matchCoresWithZonesAndAlert();
  }

  // Sergei 2025-09-18: we no longer want to do that: CU-86b6r82u3
  // mission.setPullinByBoundary();

  // initialize the drive order
  const missionSamples = cleanSet(flatten(missionWithJob.getSampleSites().map((sel) => sel.getSamples())));
  // get < -1,0,1 > result with subtraction
  missionSamples.sort((a, b) => a.sample_id!.localeCompare(b.sample_id!, 'en', { numeric: true }));

  for (const [i, sample] of missionSamples.entries()) {
    sample.order = i + 1;
  }

  return [missionWithJob, error_messages];
}

async function processPointsOrZonesFile(
  shpDataArray: shp.FeatureCollectionWithFilename[],
  confirmColumn: (item: ConfirmColumn) => Promise<string>,
  shpfile: {
    data: shp.FeatureCollectionWithFilename | shp.FeatureCollectionWithFilename[];
    mode: RogoShapefileType;
    source: string;
    name: string;
  },
  error_messages: string[],
  mission: Mission,
) {
  const originalPointsOrZonesFileProcessor = new OriginalPointsOrZonesFileProcessor(
    shpDataArray,
    confirmColumn,
    shpfile,
    error_messages,
    mission,
  );

  await originalPointsOrZonesFileProcessor.process();
}

function validateBoundaryGeometry(feature: Feature<Geometry, GeoJsonProperties>, error_messages: string[]) {
  if (!feature.geometry) {
    console.error('Feature has no geometry', feature);
    error_messages.push('Feature has no geometry');

    return false;
  }

  if (!isPolygonOrMultiPolygon(feature.geometry)) {
    console.error('Feature has invalid geometry type for boundaries', feature);
    error_messages.push(`Feature has invalid geometry type for boundaries: ${feature.geometry.type}`);

    return false;
  }

  return true;
}

async function processBoundaryFile(
  shpDataArray: shp.FeatureCollectionWithFilename[],
  mission: Mission,
  error_messages: string[],
) {
  let field = mission.getZones().find((sel) => sel.zone_type === ZoneType.FIELD);

  for (const shpdata of shpDataArray) {
    if (shpdata.type !== 'FeatureCollection') {
      console.error('Shapefile does not contain a feature collection', shpdata);
      error_messages.push(`Shapefile does not contain a feature collection: ${shpdata.fileName}.shp`);

      continue;
    }

    for (const feature of shpdata.features) {
      // @ts-ignore turf vs ol
      if (!validateBoundaryGeometry(feature, error_messages)) {
        continue;
      }

      if (!field) {
        field = Zone.create();
        field.Mission_id = mission.instance_id;
        field.zone_name = 'field';
        field.zone_type = ZoneType.FIELD;
      }

      let geom_coordinates: Position[][][];

      if (isPolygon(feature.geometry)) {
        geom_coordinates = [feature.geometry.coordinates];
      } else {
        // @ts-ignore unsure why this wouldn't be a valid geom
        geom_coordinates = feature.geometry.coordinates;
      }

      const processingErrors = await processMultipolygonCoords(mission, geom_coordinates, field);
      processingErrors.forEach((error: string) => error_messages.push(error));
    }
  }

  if (!field) {
    error_messages.push(`Field boundary was not created`);

    return;
  }

  if (!field.hasValidBoundaries()) {
    error_messages.push(`Field has invalid boundaries - please check the shapefile data`);
  }
}

// Mission.to_feature / the prevFeatures cache / CoreSignature /
// shouldRedrawCores were deleted in the KmlLayer split (Step 9 of
// docs/superpowers/plans/2026-04-08-kml-layer-split.md). Each focused sibling
// layer now reads directly from the mission (CentroidsLayer reads
// samples via sample.to_feature, CorePointsLayer reads cores via
// soilCore.to_feature, ZonesLayer reads zones via zone.to_feature,
// PathLayer walks mission.getWaypoints(), StaticPointsLayer reads the
// 4 mission point fields, DumpEventsLayer reads
// mission.getSoilDumpEvents). The monolithic mission.to_feature
// walker is no longer needed.

export async function update_or_delete_boundary(
  this: Mission,
  polyId: number,
  points: Coordinate[][],
  removeGeoLocatedCoresInside: boolean = true,
) {
  console.log(
    `update_or_delete_boundary: polyId ${polyId}, removeGeoLocatedCoresInside ${removeGeoLocatedCoresInside}`,
  );

  // Update or delete boundaries
  const missionOuterBoundaries = cleanSet(flatten(this.getZones().map((zone) => zone.getOuterZoneBoundarys())));
  const zoneOuterBoundary = missionOuterBoundaries.find((sel) => sel.poly_id === polyId);

  if (!zoneOuterBoundary) {
    this.to_ros_msg();

    return;
  }

  const missionInnerBoundaries = cleanSet(flatten(this.getZones().map((zone) => zone.getInnerZoneBoundarys())));
  const zoneInnerBoundaries = orderBy(
    missionInnerBoundaries.filter((sel) => sel.poly_id === polyId),
    ['boundary_id'],
    ['asc'],
  );

  if (points.length > 0) {
    // Update a boundary
    for (const [i, ring] of points.entries()) {
      // The 0th ring is the outer boundary
      // The 1st+ rings are the inner boundaries
      const bnd = i === 0 ? zoneOuterBoundary : zoneInnerBoundaries[i - 1];

      if (ring.length < 4) {
        bnd.dispose();
      } else {
        bnd.coordinates = ring;
      }
    }

    this.to_ros_msg();

    dispatchMissionBoundaryUpdated();

    return;
  }

  // Delete a boundary

  // for sample zones get a list of cores to delete
  const zone = zoneOuterBoundary.getOuterZone()!;

  if (zone.zone_type === ZoneType.SAMPLE_ZONE && removeGeoLocatedCoresInside) {
    zone.removeCoresGeoLocatedInSampleZone();

    // renumber waypoints
    for (const [i, waypoint] of cleanSet(orderBy(this.getWaypoints(), ['waypoint_number'], ['asc'])).entries()) {
      waypoint.waypoint_number = i + 1;
    }
  }

  // delete all inner boundaries
  for (const ib of zoneInnerBoundaries) {
    ib.dispose();
  }

  // delete outer boundary
  zoneOuterBoundary.dispose();

  // call 'to_ros_msg' to re-calculate the nav checksum
  this.to_ros_msg();

  dispatchMissionBoundaryUpdated();
  // Cores were removed in the delete path above.
  dispatchCoresNeedUpdate();
}

export function update_pullin(this: Mission, point: Coordinate) {
  this.pullin_lat = point[0];
  this.pullin_lon = point[1];
  // Narrow event — pullin change does not affect cores / centroids /
  // zones / path / dumps. Only StaticPointsLayer + MapInteractionsLayer
  // listen. See Step 12 of docs/superpowers/plans/2026-04-08-kml-layer-split.md.
  dispatchStaticPointsUpdated();
}

export function update_parking_point(this: Mission, point: Coordinate) {
  this.parking_point.lat = point[0];
  this.parking_point.lon = point[1];

  dispatchStaticPointsUpdated();
}

export function update_starting_point(this: Mission, point: Coordinate) {
  this.starting_point.lat = point[0];
  this.starting_point.lon = point[1];

  // A smart-path start (waypoint #1 without a core) was planned for the old
  // starting point; its pinned heading and the first hop's are stale.
  const first = orderBy(cleanSet(this.getWaypoints()), ['waypoint_number'], ['asc'])[0];

  if (first && !first.getCorePoint()) {
    clearBearingsAround(this, [first.waypoint_number]);
  }

  dispatchStaticPointsUpdated();
}

export function update_end_point(this: Mission, point: Coordinate) {
  this.end_point.lat = point[0];
  this.end_point.lon = point[1];

  const last = orderBy(cleanSet(this.getWaypoints()), ['waypoint_number'], ['desc'])[0];

  if (last && !last.getCorePoint()) {
    clearBearingsAround(this, [last.waypoint_number]);
  }

  dispatchStaticPointsUpdated();
}

// TODO points is really likely coordinates but number[][] is better than nothing right now
export async function update_path(this: Mission, points: number[][]) {
  await logger.log('UPDATE_PATH', `points=${JSON.stringify(points.length)}`);
  // A path update either adds a point, deletes a point, or moves a point.
  // There is no edit in in which two or more of these will happen at once.
  let waypoints = cleanSet(orderBy(this.getWaypoints(), ['waypoint_number'], ['asc']));
  // Pinned headings invalidated by this edit, as 1-based waypoint numbers
  // AFTER the renumbering below (see waypoint_bearings.ts).
  let staleAround: number[] = [];
  let staleExact: number[] = [];

  if (points.length !== waypoints.length) {
    if (points.length > waypoints.length) {
      // add a point
      for (const [i, waypoint] of waypoints.entries()) {
        if (!isclose(waypoint.lat, points[i][0]) || !isclose(waypoint.lon, points[i][1])) {
          // the first waypoint that does not match was added
          const new_waypoint = Waypoint.create();
          new_waypoint.Mission_id = this.instance_id;
          new_waypoint.waypoint_number = i + 0.5; // waypoint number non-integer here to enable sorting
          new_waypoint.lat = points[i][0];
          new_waypoint.lon = points[i][1];
          const pathpoint = PathPoint.create();
          pathpoint.Waypoint_id = new_waypoint.instance_id;
          staleAround = [i + 1]; // the new waypoint's number once renumbered
          break; // only one point can be added at a time
        }
      }
    } else if (points.length < waypoints.length) {
      // delete a point
      const patternType = this.getPrimarySpec()?.pattern_type;

      for (const [i, waypoint] of waypoints.entries()) {
        if (i >= points.length || !isclose(waypoint.lat, points[i][0]) || !isclose(waypoint.lon, points[i][1])) {
          const corePoint = waypoint.getCorePoint();

          if (!corePoint || (corePoint && patternType === GridPatterns.ZONE)) {
            // do not delete core points
            // the first waypoint that does not match was deleted
            if (corePoint) {
              corePoint?.getSoilCore()?.dispose();
            } else {
              waypoint.dispose();
            }

            // Its two neighbours now form one hop neither was planned for.
            staleExact = [i, i + 1];
          }

          break; // only one point can be added at a time
        }
      }
    }

    // reorder waypoints
    waypoints = cleanSet(orderBy(this.getWaypoints(), ['waypoint_number'], ['asc']));

    for (const [i, waypoint] of waypoints.entries()) {
      waypoint.waypoint_number = i + 1;
    }
  } else {
    // move a point
    // TODO
    const patternType = this.getPrimarySpec()?.pattern_type;

    if (!patternType) {
      throw Error('Primary spec not found');
    }

    for (const [i, waypoint] of waypoints.entries()) {
      if (!isclose(waypoint.lat, points[i][0]) || !isclose(waypoint.lon, points[i][1])) {
        // Currently assumes only one spec in the mission
        if (!waypoint.getCorePoint()) {
          // allow moving path points
          waypoint.lat = points[i][0];
          waypoint.lon = points[i][1];
          staleAround.push(i + 1);
        } else if (i === 0 || i === waypoints.length - 1) {
          // if it is the first or last waypoint, create a new point before/after the core location
          const wp_num = i === 0 ? 0 : waypoints.length + 1;
          const new_waypoint = Waypoint.create();
          new_waypoint.Mission_id = this.instance_id;
          new_waypoint.waypoint_number = wp_num;
          new_waypoint.lat = points[i][0];
          new_waypoint.lon = points[i][1];
          const pathpoint = PathPoint.create();
          pathpoint.Waypoint_id = new_waypoint.instance_id;
          // reorder waypoints
          waypoints = cleanSet(orderBy(this.getWaypoints(), ['waypoint_number'], ['asc']));

          for (const [i, waypoint] of waypoints.entries()) {
            waypoint.waypoint_number = i + 1;
          }

          staleAround.push(i === 0 ? 1 : waypoints.length);
        } else if (patternType === GridPatterns.ZONE) {
          // allow moving of core locations for zone sampling, but if it is the first core,
          // still create a lead in point instead of moving the core.
          // reject the edit if it moves it out of the zone
          const core = waypoint.getCorePoint()?.getSoilCore();
          const zone = core?.getSample()?.getSampleSite()?.getSampleZone()?.getZone();

          if (core && zone?.contains(points[i][0], points[i][1])) {
            waypoint.lat = points[i][0];
            waypoint.lon = points[i][1];
            core.lat = waypoint.lat;
            core.lon = waypoint.lon;
            staleAround.push(i + 1);
          }
        }
      }
    }
  }

  clearBearingsAround(this, staleAround);
  clearBearings(this, staleExact);

  // call 'to_ros_msg' to re-calculate the nav checksum
  await this.to_ros_msg();
  dispatchMissionUpdated();
  // update_path may have added/removed waypoints, moved cores, or
  // changed core.lat/core.lon inside zone sampling — CorePointsLayer
  // needs to rebuild.
  dispatchCoresNeedUpdate();
}

export async function add_stop_core_waypoint(
  core: SoilCore,
  missionId: number,
  waypointIndex: number,
  coreNumber: number,
) {
  const waypoint = Waypoint.create();
  waypoint.Mission_id = missionId;
  waypoint.waypoint_number = waypointIndex;
  waypoint.lat = core.lat;
  waypoint.lon = core.lon;

  const core_point = CorePoint.create();
  core_point.Waypoint_id = waypoint.instance_id;
  core_point.SoilCore_id = core.instance_id;
  core.core_number = coreNumber;
  core.waypoint_index = waypointIndex;
}

export function create_new_core(latLon: number[], sample: Sample, source: CoreSource = 'Unknown'): SoilCore {
  const createdCore = SoilCore.create();
  createdCore.Sample_id = sample.instance_id;
  createdCore.core_number = sample.getNextAvailableCoreNumber();
  createdCore.source = source;
  createdCore.lat = latLon[0];
  createdCore.lon = latLon[1];

  return createdCore;
}

export function create_new_core_with_waypoint_and_core_point(
  latLon: number[],
  missionInstanceId: number,
  sample: Sample,
  waypointIndex: number,
  source: CoreSource = 'Unknown',
  addIntermediateWaypoints: (() => number) | undefined = undefined, // this is a functio that can be used to add intermediate waypoints
  // The robot-frame approach azimuth to pin on the new waypoint (position.z).
  // Extras placed on a planned cluster line pass the original's bearing so the
  // robot drives that line instead of deriving a heading from its neighbours.
  bearingAzimuthDeg: number | undefined = undefined,
): SoilCore {
  const createdCore = create_new_core(latLon, sample, source);
  let newWaypointIndex = waypointIndex;

  if (addIntermediateWaypoints) {
    newWaypointIndex = addIntermediateWaypoints();
  }

  const waypoint = Waypoint.create();
  waypoint.Mission_id = missionInstanceId;
  waypoint.waypoint_number = newWaypointIndex;
  waypoint.lat = latLon[0];
  waypoint.lon = latLon[1];

  if (bearingAzimuthDeg !== undefined && Number.isFinite(bearingAzimuthDeg)) {
    waypoint.bearing_azimuth_deg = bearingAzimuthDeg;
  }

  const corePoint = CorePoint.create();
  corePoint.Waypoint_id = waypoint.instance_id;
  corePoint.SoilCore_id = createdCore.instance_id;
  createdCore.waypoint_index = newWaypointIndex;

  return createdCore;
}

// Wire convention for waypoint position.z: the optional explicit approach
// bearing in the ROBOT's convention — lon/lat azimuth, DEGREES, (0, 360)
// — exactly as pathfinder computed it (bearingAzimuthDeg, its
// robot_adapter::to_robot_bearing_deg = the robot's Mapper::to_azimuth at
// the waypoint's latitude). Sentinel value 400 (any value > 360) means "no
// explicit bearing — use neighbor-based derivation (calc_waypoint_angle on
// the robot)". The robot reads position.z into Waypoint.bearing and uses
// it when ≤ 360, falling through to the historical neighbor rules
// otherwise. Never send a metric-frame heading here: the robot would push
// it through from_azimuth as if it were an azimuth.
export const NO_BEARING_SENTINEL = 400;

/**
 * The value to pack into a robot waypoint's position.z: the pinned azimuth
 * verbatim, or the 400 sentinel when there is none. A finite
 * `Waypoint.bearing_azimuth_deg` is always sent — the old 'Use Wp Explicit
 * Bearing' technician toggle is gone.
 */
export function bearing_to_z(bearing_azimuth_deg: number | undefined): number {
  if (bearing_azimuth_deg === undefined || !Number.isFinite(bearing_azimuth_deg)) {
    return NO_BEARING_SENTINEL;
  }

  return bearing_azimuth_deg;
}

export function to_ros_msg(this: Mission, frameID: FrameID = 'latlon') {
  // build boundaries
  const outer_boundaries: RosBoundary[] = [];
  const inner_boundaries: RosBoundary[] = [];
  const waypoints: RosWaypoint[] = [];
  const errors: string[] = [];

  if (!this) {
    return;
  }

  const missionJob = this.getJob();
  const usingCarbonProbe = this.usesCarbonProbe();
  const CORE_COMMAND = usingCarbonProbe ? BD_CORE_COMMAND : 1;
  const CORE_AND_DUMP_COMMAND = usingCarbonProbe ? BD_CORE_COMMAND : 2;

  for (const ob of cleanSet(
    flatten(
      this.getZones()
        .filter((sel) => sel.zone_type === ZoneType.FIELD)
        .map((z) => z.getOuterZoneBoundarys()),
    ),
  )) {
    outer_boundaries.push({ points: ob.coordinates.map((coord) => ({ x: coord[1], y: coord[0], z: 0 })) });
  }

  for (const ib of cleanSet(
    flatten(
      this.getZones()
        .filter((sel) => sel.zone_type === ZoneType.FIELD)
        .map((z) => z.getInnerZoneBoundarys()),
    ),
  )) {
    inner_boundaries.push({ points: ib.coordinates.map((coord) => ({ x: coord[1], y: coord[0], z: 0 })) });
  }

  // Add unsafe and slow zones to inner boundaries
  for (const ob of cleanSet(
    flatten(
      this.getZones()
        .filter((sel) => sel.zone_type === ZoneType.UNSAFE || sel.zone_type === ZoneType.SLOW)
        .map((z) => z.getOuterZoneBoundarys()),
    ),
  )) {
    inner_boundaries.push({ points: ob.coordinates.map((coord) => ({ x: coord[1], y: coord[0], z: 0 })) });
  }

  // build waypoints
  const missionWaypoints = cleanSet(orderBy(this.getWaypoints(), ['waypoint_number'], ['asc']));
  const primarySpec = this.getPrimarySpec();
  const numCoresPerSample = primarySpec.cores || 0;

  let doubleSoilDump = false;

  if (!this.getJob()?.zone_interleave && numCoresPerSample * primarySpec.end_depth >= MIDPOINT_DUMP_MIN_INCHES) {
    doubleSoilDump = true;
  }

  const coreMap: Record<string, SoilCore[]> = {};

  for (const [index, wp] of missionWaypoints.entries()) {
    const cp = wp.getCorePoint();
    const core = cp ? cp.getSoilCore() : null;

    // if this waypoint is also a core point, then we either need to take a core (1) or dump (2)
    if (core) {
      const nextCoreWaypoint = missionWaypoints.find(
        (wp, nextIndex) => nextIndex > index && !!wp.getCorePoint()?.getSoilCore(),
      );
      // if there is no more core waypoints, the last core should be a dump command anyways
      let command = CORE_COMMAND;

      // otherwise, if we know the next core point, we should see if its sample ID is different than our current sample ID
      const currentSample = core.getSample();

      if (!currentSample) {
        errors.push(`waypoint[${index}]: core does not have a sample`);

        continue;
      }

      const currentSampleId = currentSample.sample_id;
      const nextCoreSampleId = nextCoreWaypoint?.getCorePoint()?.getSoilCore()?.getSample()?.sample_id;

      if (!currentSampleId) {
        errors.push(`waypoint[${index}]: core does not have a sample ID`);

        continue;
      }

      const changingSampleIds = currentSampleId !== nextCoreSampleId;

      if (changingSampleIds) {
        command = CORE_AND_DUMP_COMMAND;
      }

      if (!(currentSampleId in coreMap)) {
        coreMap[currentSampleId] = [];
      }

      coreMap[currentSampleId].push(core);

      const coresSeen = coreMap[currentSampleId] || [];
      const coreNumber = coresSeen.length;

      if (doubleSoilDump && coreNumber === Math.round(numCoresPerSample / 2)) {
        command = CORE_AND_DUMP_COMMAND;
      }

      waypoints.push({
        sample_id: currentSampleId,
        command: command,
        position: {
          x: wp.lon,
          y: wp.lat,
          z: bearing_to_z(wp.bearing_azimuth_deg),
        },
      });
    } else {
      // otherwise this is just a normal navigation waypoint (command = 0)
      waypoints.push({
        sample_id: '',
        command: 0,
        position: {
          x: wp.lon,
          y: wp.lat,
          z: bearing_to_z(wp.bearing_azimuth_deg),
        },
      });
    }
  }

  // generate the nav checksum
  this.nav_checksum = hash.sha1({ outer_boundaries, inner_boundaries, waypoints });

  const sampling_tolerance_mm = missionJob ? Math.round(missionJob.sampling_tolerance_ft * MM_PER_FT) : 0;

  // build the mission
  const spec = this.getPrimarySpec();
  const utm_loc = fromLatLon(this.pullin_lat, this.pullin_lon);
  const localSetUseArmCenteredNav = MapCalculationPositionStorage.get() === 'Arm';
  const useArmCenteredNav = missionJob?.implement_centerted_navigation || localSetUseArmCenteredNav;
  const mission: RosMissionMsgWithFieldParam = {
    header: {
      frame_id: frameID,
    },
    name: this.name,
    job_id: this.job_id || '',
    last_modified: this.last_modified,
    utm_zone: `${utm_loc.zoneNum}${utm_loc.zoneLetter}`,
    nav_checksum: this.nav_checksum,
    outer_boundaries: outer_boundaries,
    inner_boundaries: inner_boundaries,
    waypoints,
    samples: cleanSet(flatten(this.getSampleSites().map((ss) => ss.getSamples()))).map((s) => {
      return {
        header: {
          stamp: { secs: 0, nsecs: 0 },
        },
        job_id: this.job_id,
        sample_id: s.getSampleIdCombinedWithExternalRef(),
        barcode_id: s.bag_id,
        start_depth: 0, // TODO hard coded for single depth sampling
        end_depth: spec?.end_depth || 0,
      } as RosSample;
    }),
    field_param: [
      (missionJob?.field_aligned_sample_path || 0) |
        (missionJob?.plot_mission ? FieldParams.NoBoundaryTolerance : 0) |
        (useArmCenteredNav ? FieldParams.ImplementCenteredNavigation : 0),
      sampling_tolerance_mm,
    ],
  };

  // console.log('to_ros msg', mission);
  // console.log('Boundary sent to robot: ');
  // console.log('outer_bound', outer_boundaries);
  // console.log('inner_bound', inner_boundaries);
  // console.log(JSON.stringify(genBoundarySentToRobot(outer_boundaries, inner_boundaries)));

  errors.push(...validateRosMission(mission));

  if (errors.length > 0) {
    const errorMessage = `to_ros_msg failed: ${errors.join('; ')}`;
    console.error(errorMessage);
    alertError(errorMessage);

    return undefined;
  }

  return mission;
}

/**
 * Validate ROS mission message fields against their ROS msg type constraints.
 * See: rogo_msgs/msg/CUD_Mission.msg, Waypoint.msg, Sample.msg
 *
 * Prevents the ROS bridge from breaking on type overflow (e.g. uint32 field_param).
 */
export function validateRosMission(mission: RosMissionMsgWithFieldParam): string[] {
  const UINT32_MAX = 4294967295;
  const UINT8_MAX = 255;
  const errors: string[] = [];

  const isValidUint32 = (v: number) => Number.isInteger(v) && v >= 0 && v <= UINT32_MAX;
  const isValidUint8 = (v: number) => Number.isInteger(v) && v >= 0 && v <= UINT8_MAX;
  const isFinitePositive = (v: number) => Number.isFinite(v) && v >= 0;

  // --- Airtable-sourced fields (user-editable, highest risk) ---

  // CUD_Mission.msg: uint32[] field_param
  for (const [i, val] of mission.field_param.entries()) {
    if (!isValidUint32(val)) {
      errors.push(`field_param[${i}]=${val} is not a valid uint32 (0..${UINT32_MAX})`);
    }
  }

  // Sample.msg: float64 start_depth, float64 end_depth
  for (const sample of mission.samples) {
    if (!isFinitePositive(sample.end_depth)) {
      errors.push(`sample ${sample.sample_id}: end_depth=${sample.end_depth} must be finite and >= 0`);
    }

    if (!isFinitePositive(sample.start_depth)) {
      errors.push(`sample ${sample.sample_id}: start_depth=${sample.start_depth} must be finite and >= 0`);
    }
  }

  // --- Computed fields (safety net) ---

  // Waypoint.msg: uint8 command
  for (const [i, wp] of mission.waypoints.entries()) {
    if (!isValidUint8(wp.command)) {
      errors.push(`waypoint[${i}] sample_id=${wp.sample_id}: command=${wp.command} is not a valid uint8`);
    }
  }

  // Waypoint.msg: geometry_msgs/Point position (float64 x, y, z)
  for (const [i, wp] of mission.waypoints.entries()) {
    const { x, y, z } = wp.position;

    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
      errors.push(`waypoint[${i}] sample_id=${wp.sample_id}: non-finite position (${x}, ${y}, ${z})`);
    }
  }

  // CUD_Mission.msg: geometry_msgs/Polygon[] → Point32 (float32 x, y, z)
  const validateBoundaryPoints = (label: string, boundaries: RosBoundary[]) => {
    for (const [i, boundary] of boundaries.entries()) {
      for (const [j, pt] of boundary.points.entries()) {
        if (!Number.isFinite(pt.x) || !Number.isFinite(pt.y)) {
          errors.push(`${label}[${i}].points[${j}]: non-finite coordinates (${pt.x}, ${pt.y})`);
        }
      }
    }
  };

  validateBoundaryPoints('outer_boundary', mission.outer_boundaries);
  validateBoundaryPoints('inner_boundary', mission.inner_boundaries);

  // CUD_Mission.msg: float64 last_modified
  if (!Number.isFinite(mission.last_modified) || mission.last_modified <= 0) {
    errors.push(`last_modified=${mission.last_modified} must be a positive finite number`);
  }

  return errors;
}

export function is_zone_mission(this: Mission) {
  // TODO this is hard coded 'get the 0th sampling spec' logic, but this is actually
  // an instance where it's probably okay because upstream logic would not allow grid and
  // modgrid to be mixed with zone specs
  const missionSpec = this.getPrimarySpec();

  return missionSpec?.pattern_type === GridPatterns.ZONE || false;
}

export async function update_job_details_with_airtable(mission: Mission, missionJob: Job) {
  if (!mission.job_id) {
    return;
  }

  const job = await crmBase.jobs.get(mission.job_id);

  if (!job) {
    return;
  }

  mission.name = job.missionName ?? '';
  missionJob.test_package = job.testPckgDashFinal ? job.testPckgDashFinal[0] || '' : '';
  missionJob.event_id = job.eventId ?? '';
  missionJob.sampling_company_name = 'Rogo';
  missionJob.sampling_company_id = '';
  missionJob.response_email = job.emailForResults ? job.emailForResults[0] || '' : '';
  missionJob.client = job.client ? job.client[0] || '' : '';
  missionJob.grower = job.growerName ? job.growerName[0] || '' : '';
  missionJob.farm = job.farmNameClean ?? '';
  missionJob.field = job.fieldNameClean ?? '';
  missionJob.in_field_notes_ops = job.inFieldNotes ?? '';
  missionJob.tilled_field_at_sampling = job.isTilledFieldAtSampling ?? false;

  missionJob.boundary_change_type = (job.boundaryChangeType ?? '') as BoundaryChangeType;
  missionJob.boundary_change_notes = job.boundaryChangeNotes ?? '';
  missionJob.field_id = job.fieldId ?? '';
  missionJob.api_id = job.apiId ?? '';
  missionJob.add_on_freq = job.finalFreqOfAddDashOnTestPckg?.[0] ?? 1;

  const addOnTestPckgMicrosList = (job.addDashOnTestPckgMicrosList ?? '').trim();
  missionJob.micros_list = addOnTestPckgMicrosList ? addOnTestPckgMicrosList.split(',') : [];

  missionJob.sample_order_type = job.pdfOrderType ?? '';
  missionJob.submitter_notified_id = job.submitterIdFinal ?? '';

  missionJob.sites_type = job.dealSitesType?.[0] ?? '';

  missionJob.map_making_rules = '';

  if (missionJob.deal_name) {
    missionJob.map_making_rules += missionJob.deal_name;
  }

  // this field MUST be set before we look at points_submitted_from_customer or zones_interleave because
  // they are both just shadow properties that look at this field
  missionJob.required_fields = job.requiredFieldsJobPlusDeal ?? '';
  missionJob.job_flags = (job.jobFlags ?? []) as string[];
  const callPriorToScheduleValue = job.callPriorToScheduleQFromClientPlusDeal?.[0];
  missionJob.call_prior_to_schedule =
    callPriorToScheduleValue === true || missionJob.job_flags.includes('Call Prior To Schedule');

  const fieldNotes = job.fieldNotes ?? [];

  if (fieldNotes.length > 0) {
    missionJob.map_making_rules += `Field Notes: `;
    let count = 1;

    for (const note of fieldNotes) {
      // format each attachment as an href link with the text being #1, #2, etc on the same line
      // open the attachment in a new tab
      missionJob.map_making_rules += `<a href="${note.url}" target="_blank">#${count++}</a> `;
    }

    missionJob.map_making_rules += '\n';
  }

  seedPullinFromJobLocation(mission, job);
  await seedZonesFromMapFeatures(mission, job);

  return job;
}

/** `Lat Lon Source` values that mean a PERSON placed the point: a dispatcher's cluster pull-in
 *  from portal scheduling, or a mapmaker's KML point (road-snapped or not). `CenterBoundary` is
 *  the boundary lambda's intake centroid — a placeholder, not a pull-in. */
const PLACED_PULLIN_SOURCES: readonly string[] = ['Pullin', 'NearestRoad'];

/** Parse a `Jobs.Location` string (`"lat,lng"`) into coordinates, or `undefined` if it is not two
 *  finite, in-range parts. MC's other four read sites split this field blindly; this one does not,
 *  because the value becomes a navigation target. */
function parseJobLocation(location: string | undefined): { lat: number; lon: number } | undefined {
  if (!location) {
    return undefined;
  }

  const parts = location.split(',');

  if (parts.length !== 2 || !parts[0].trim() || !parts[1].trim()) {
    return undefined;
  }

  const lat = Number(parts[0]);
  const lon = Number(parts[1]);

  if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) {
    return undefined;
  }

  return { lat, lon };
}

/**
 * Seed the mission's pull-in from the job's `Jobs.Location`, which a dispatcher can set for a
 * whole cluster at scheduling time.
 *
 * Two guards, both load-bearing:
 *
 * 1. Only when the mission has no pull-in of its own. This runs on every `update_mission_details`,
 *    including each reopen of an existing mission, so without it a mapmaker's saved KML point
 *    would be overwritten from Airtable every time they opened their own map.
 * 2. Only from a source a person placed. Seeding from a `CenterBoundary` centroid would re-create
 *    `setPullinByBoundary()` — the behaviour deliberately disabled in CU-86b6r82u3 — just sourced
 *    remotely instead of locally.
 */
function seedPullinFromJobLocation(mission: Mission, job: { location?: string; latLonSource?: string }): void {
  if (mission.isPullinValid() || !PLACED_PULLIN_SOURCES.includes(job.latLonSource ?? '')) {
    return;
  }

  const point = parseJobLocation(job.location);

  if (!point) {
    return;
  }

  mission.pullin_lat = point.lat;
  mission.pullin_lon = point.lon;
}

/**
 * Fetch all lab records needed by both update_job_with_lab_details and
 * updateImmutableSamplingSpecPropsFromAt in a single Airtable call.
 */
async function fetchLabsByName(atJob: AtJob, deals: AtDeal[]): Promise<Map<string, AtLab>> {
  const labNames = new Set<string>();

  const primaryLabName = (atJob.labDashFinal ?? '').replace(/"/g, '');

  if (primaryLabName) {
    labNames.add(primaryLabName);
  }

  for (const deal of deals) {
    const dealLabName = (deal.labName?.[0] ?? '').replace(/"/g, '');

    if (dealLabName) {
      labNames.add(dealLabName);
    }
  }

  if (labNames.size === 0) {
    return new Map();
  }

  const conditions = Array.from(labNames).map((name) => `{Name} = '${name}'`);
  const formula = conditions.length === 1 ? conditions[0] : `OR(${conditions.join(', ')})`;

  const labRecords = await crmBase.labs.get({ view: 'App View - Labs', formula });
  const labsByName = new Map<string, AtLab>();

  if (labRecords) {
    for (const lab of Array.isArray(labRecords) ? labRecords : [labRecords]) {
      if (lab.name) {
        labsByName.set(lab.name, lab);
      }
    }
  }

  return labsByName;
}

export async function update_job_with_lab_details(missionJob: Job, atJob: AtJob, labsByName: Map<string, AtLab>) {
  const primaryLabName = (atJob.labDashFinal ?? '').replace(/"/g, '');
  missionJob.lab_name = primaryLabName || missionJob.lab_name;

  // update lab details
  // TODO(multi-lab): these Job-level lab fields are now also set per-spec in
  // updateImmutableSamplingSpecPropsFromAt. Consumers should migrate to reading
  // from SamplingSpec. Keep Job-level values for backward compat (primary lab).
  missionJob.billing_account = atJob.finalLabAccNumber?.[0] ?? '';
  missionJob.lab_short_name = (atJob.labShortname?.[0] ?? '').replace(/"/g, '');
  missionJob.lab_code = atJob.labCode?.[0] ?? '';
  missionJob.lab_instructions = atJob.labInstructions ?? '';
  missionJob.lab_submittal_id = atJob.finalLabPresubmissionCode ?? '';
  missionJob.lab_address = atJob.labAddress?.[0] ?? '';

  const labRecord = labsByName.get(primaryLabName);

  if (!labRecord) {
    alertWarn(`Unable to retrieve lab record when updating mission details`);

    return;
  }

  // TODO: all these should not be needed if we handle multi-lab case thoroughly:
  // those should be spec props, not job's
  missionJob.lab_address = labRecord.address ?? missionJob.lab_address;
  missionJob.lab_name = labRecord.name ?? '';
  missionJob.lab_short_name = labRecord.labShortcutName ?? '';
  missionJob.lab_code = labRecord.labCode ?? '';
  missionJob.lab_address = labRecord.address ?? '';
  missionJob.lab_primary_delivery = labRecord.primaryDeliveryMethod ?? atJob.labPrimaryDeliveryMethod?.[0] ?? '';
  missionJob.lab_qrs_required = labRecord.labOptions?.includes('QR Required') ?? false;
  missionJob.auto_renumber = labRecord.labOptions?.includes('Auto Renumber') ?? false;
  missionJob.auto_fill_lab_instructions = labRecord.labOptions?.includes('Auto Fill Lab Instructions') ?? false;
}

export async function update_job_with_company_details(missionJob: Job, atJob: AtJob) {
  const companyRecordId = atJob.clientOriginalName?.[0];

  if (!companyRecordId) {
    return;
  }

  const companyRecords = await crmBase.companies.get({
    view: 'Active Companies - Deals OR Customer Status Current',
    formula: Company.f.id.equals(companyRecordId),
  });
  const companyRecord = companyRecords?.[0];

  if (!companyRecord) {
    alertWarn(`Unable to retrieve company record when updating mission details`);

    return;
  }

  const companyMapMakingRules = companyRecord.mapmakingRules;

  if (companyMapMakingRules) {
    missionJob.map_making_rules += 'Company Mapmaking Rules\n';
    missionJob.map_making_rules += '- ' + companyMapMakingRules;
    missionJob.allowed_to_renumber = companyRecord.isRenumberPoints ?? false;
    missionJob.allowed_to_create = companyRecord.isCreatePoints ?? false;
    missionJob.allowed_to_move = companyRecord.isMovePoints ?? false;
  }

  missionJob.external_reference_id_column = companyRecord.externalSampleReferenceIdColumn ?? '';

  return companyRecord;
}

async function update_job_with_deal(missionJob: Job, atJob: AtJob): Promise<AtDeal[]> {
  const dealId = atJob.deal?.id;

  if (!dealId) {
    return [];
  }

  const primaryDeals = await crmBase.deals.get({
    view: 'App View - Active Deals',
    formula: `RECORD_ID() = '${dealId}'`,
  });
  const primaryDeal = primaryDeals?.[0];

  if (!primaryDeal) {
    return [];
  }

  missionJob.core_diameter = (primaryDeal.coreDiameter ?? '0.75 in') as CoreDiameter;
  const dealFieldParams = primaryDeal.fieldParameters ?? [];
  const useFieldAlignedSamplePath = dealFieldParams.includes('Field Aligned Sample Path');

  if (useFieldAlignedSamplePath) {
    missionJob.field_aligned_sample_path = FieldParams.FieldAlignedSamplePath;
  }

  missionJob.manual_hole_depth_measurement = dealFieldParams.includes('Manual Hole Depth Measurement');
  // missionJob.manual_plunge_depth_measurement = dealFieldParams.includes('Manual Plunge Depth Measurement');
  missionJob.manual_core_length_measurement = dealFieldParams.includes('Manual Core Length Measurement');
  missionJob.manual_core_loss_measurement = dealFieldParams.includes('Manual Core Loss Measurement');
  missionJob.plot_mission = dealFieldParams.includes('Plot Missions');
  missionJob.disable_core_line_alteration = dealFieldParams.includes('Disable Core Line Alteration');
  missionJob.use_original_sample_id = dealFieldParams.includes('Use Original Sample ID');
  missionJob.sampling_tolerance_ft = primaryDeal.samplingToleranceFt ?? 0;
  missionJob.sampling_type_special = primaryDeal.samplingTypeSpecial ?? '';
  missionJob.implement_centerted_navigation = dealFieldParams.includes('Implement Centered Navigation');
  missionJob.deal_name = primaryDeal.name ?? '';

  if (missionJob.deal_name) {
    missionJob.map_making_rules += `\n  - Deal Name: <b>${missionJob.deal_name}</b>\n\n`;
  }

  const mappingRulesFromDeal = primaryDeal.mappingRules;

  if (mappingRulesFromDeal) {
    missionJob.map_making_rules += `Deal Mapping Rules:\n-${mappingRulesFromDeal}\n\n`;
  }

  missionJob.enable_manual_drive_aid = dealFieldParams.includes('Manual Drive Aids');
  missionJob.auto_zoom_near_sample = dealFieldParams.includes('Auto Zoom Near Sample');

  const secondaryDealIds = primaryDeal.additionalSamplingConfigurations?.ids ?? [];
  const secondaryDeals = (
    await Promise.all(
      secondaryDealIds.map((dealId) =>
        crmBase.deals.get({ view: 'App View - Active Deals', formula: `RECORD_ID() = '${dealId}'` }),
      ),
    )
  )
    .map((deals) => deals?.[0])
    .filter((deal): deal is AtDeal => deal !== undefined);

  return [primaryDeal, ...secondaryDeals];
}

async function update_mapmaking_rules(missionJob: Job, atJob: AtJob) {
  const submissionNotes = atJob.submissionNotes;
  const samplingRestrictionNotes = atJob.samplingRestrictionNotes;
  const parkingFieldAccessNotes = atJob.parkingFieldAccessNotes;
  const boundaryCollection = atJob.isMajorBoundaryCollection;

  if (submissionNotes) {
    missionJob.map_making_rules += `Submission Notes:\n-${submissionNotes}\n\n`;
  }

  if (missionJob.points_submitted_from_customer) {
    missionJob.map_making_rules += `Points are or should be submitted; if not showing up, flag issue.\n\n`;
  }

  if (missionJob.zone_interleave) {
    missionJob.map_making_rules += `Zones CAN be interleaved.\n\n`;
  }

  if (boundaryCollection === 1) {
    missionJob.map_making_rules += `BOUNDARY COLLECTION: Be extra careful of boundary & safe zones\n\n`;
  }

  if (samplingRestrictionNotes) {
    missionJob.map_making_rules += `Sampling Restriction Notes:\n-${samplingRestrictionNotes}\n\n`;
  }

  if (parkingFieldAccessNotes) {
    missionJob.map_making_rules += `Parking / Field Access Notes:\n-${parkingFieldAccessNotes}\n\n`;
  }

  if (missionJob.lab_instructions) {
    missionJob.map_making_rules += `Lab Instructions:\n-${missionJob.lab_instructions}\n\n`;
  }

  const branchRecordId = atJob.branch?.id;

  if (branchRecordId) {
    const branch = await crmBase.branches.get(branchRecordId);
    const mobileNumber = branch?.mobilePhoneFromBranchContact?.[0];
    const email = branch?.branchContactImmediateJobCreationConfirmationEmails?.[0];

    if (missionJob.map_making_rules && (email || mobileNumber)) {
      missionJob.map_making_rules += '\n\n';
    }

    if (email || mobileNumber) {
      missionJob.map_making_rules += 'Branch Contact:\n';
    }

    if (email) {
      missionJob.map_making_rules += `- Email: ${email}`;
    }

    if (mobileNumber) {
      missionJob.map_making_rules += `\n- Mobile: ${mobileNumber}`;
    }
  }
}

function update_sample_types(mission: Mission, atJob: AtJob) {
  const samplingType = atJob.samplingTypeFinal;

  if (samplingType) {
    for (const sample of cleanSet(flatten(mission.getSampleSites().map((sel) => sel.getSamples())))) {
      if (samplingType === 'ISNT 1') {
        sample.sample_type = SamplingTypes.ISNT_1;
      } else if (samplingType === 'ISNT 2') {
        sample.sample_type = SamplingTypes.ISNT_2;
      } else if (samplingType === 'Cyst') {
        sample.sample_type = SamplingTypes.CYST;
      } else {
        sample.sample_type = SamplingTypes.REGULAR;
      }
    }
  }
}

async function create_sampling_specs(mission: Mission, deals: AtDeal[]) {
  if (!deals.length) {
    return;
  }

  const createSpecFromDeal = async (deal: AtDeal, primary_spec = false) => {
    const zoneMission = deal.type === 'Zone';
    const pattern = deal.patternHServiceinfo;
    const circlePattern = pattern === 'Circle';
    const dealBarcodeType = await getDealBarcodeType(deal);
    let patternType: GridPatterns = GridPatterns.LINE;

    if (pattern && zoneMission) {
      // this combination of configurations is not possible
      const errorMessage = `Zone mission with pattern ${pattern} is not possible`;
      alertError(errorMessage);

      throw new Error(errorMessage);
    }

    if (zoneMission) {
      patternType = GridPatterns.ZONE;
    } else if (circlePattern) {
      patternType = GridPatterns.CIRCLE;
    } else {
      patternType = GridPatterns.LINE;
    }

    const spec = SamplingSpec.createWithParams({
      spec_id: uuidv4(),
      deal_id: deal.id,
      deal_name: deal.name ?? '',
      primary_spec,
      Mission_id: mission.instance_id,
      pattern_type: patternType,
      cores: deal.coresHServiceinfo ?? 0,
      radius: deal.patternSizeFtHServiceinfo ?? 0,
      length: deal.patternSizeFtHServiceinfo ?? 0,
      angle: deal.patternAngleDegFromEastHServiceinfo ?? 0,
      start_depth: 0.0,
      end_depth: deal.depth ?? 0,
      lab_name: deal.labName?.[0] ?? '',
      lab_short_name: deal.labShortname?.[0] ?? '',
      lab_address: deal.labAddress?.[0] ?? '',
      lab_code: deal.labCode?.[0] ?? '',
      test_package: deal.testPckgDisplay ?? '',
      barcode_regex: dealBarcodeType?.regex ?? '',
      parallel_line_distance_ft: FEET_BETWEEN_CORE_LINES_FOR_MULTI_LAB,
    });

    return spec;
  };

  const primaryDeal = deals[0];
  await createSpecFromDeal(primaryDeal, true);

  // Create additional specs
  if (deals.length > 1) {
    const secondaryDeals = deals.slice(1);

    for (const deal of secondaryDeals) {
      await createSpecFromDeal(deal);
    }
  }
}

function updateMutableSamplingSpecPropsFromAt(mission: Mission, atJob: AtJob) {
  const dealSitesType = atJob.dealSitesType?.[0];
  const zoneMission = dealSitesType === 'Zone';

  for (const spec of cleanSet(mission.getSamplingSpecs())) {
    if (!zoneMission) {
      spec.radius = atJob.dealPatternSize?.[0] ?? 0;
      spec.length = atJob.dealPatternSize?.[0] ?? 0;
      spec.angle = atJob.finalAngleCcwFromE?.[0] ?? 0;
    }

    spec.cores = atJob.dealCores?.[0] ?? 0;
  }
}

async function updateImmutableSamplingSpecPropsFromAt(
  mission: Mission,
  atJob: AtJob,
  deals: AtDeal[],
  labsByName: Map<string, AtLab>,
) {
  const patternStr = atJob.dealPattern?.[0];
  const dealSitesType = atJob.dealSitesType?.[0];
  const zoneMission = dealSitesType === 'Zone';

  for (const spec of cleanSet(mission.getSamplingSpecs())) {
    if (zoneMission) {
      spec.pattern_type = GridPatterns.ZONE;
    } else {
      spec.pattern_type = patternStr === 'Circle' ? GridPatterns.CIRCLE : GridPatterns.LINE;
    }

    // Depths are not really immutable - a user can change them, however per Stephen:
    // "Operators have a depth override, but it's not used regularly and we don't necessarily want to persist that value"
    spec.start_depth = 0.0;
    spec.end_depth = atJob.depth?.[0] ?? 0;

    // Update test packages
    const specAtDeal = deals.find((deal) => deal && deal.id === spec.deal_id);

    if (specAtDeal) {
      const dealBarcodeType = await getDealBarcodeType(specAtDeal);

      spec.deal_name = specAtDeal.name ?? '';
      spec.test_package = specAtDeal.testPckgDisplay ?? '';
      spec.lab_name = specAtDeal.labName?.[0] ?? '';
      spec.lab_short_name = specAtDeal.labShortname?.[0] ?? '';
      spec.lab_address = specAtDeal.labAddress?.[0] ?? '';
      spec.lab_code = specAtDeal.labCode?.[0] ?? '';
      spec.barcode_regex = dealBarcodeType?.regex ?? '';

      // Per-spec lab details (fixes multi-lab: previously only primary lab was stored on Job)
      const specLabName = (specAtDeal.labName?.[0] ?? '').replace(/"/g, '');
      const labRecord = labsByName.get(specLabName);

      if (labRecord) {
        spec.lab_primary_delivery = labRecord.primaryDeliveryMethod ?? '';
        spec.lab_qrs_required = labRecord.labOptions?.includes('QR Required') ?? false;
        spec.lab_auto_renumber = labRecord.labOptions?.includes('Auto Renumber') ?? false;
      }
    }
  }
}

function checkIfDealStructureChangedTooMuch(
  before: { primaryDealId: string; secondaryDealIds: string[] },
  after: { primaryDealId: string; secondaryDealIds: string[] },
): boolean {
  // Right now, the only allowed change is when there is only one deal for a job
  // w/out any dependent deals (even if they are equal). This single deal is
  // allowed to be changed to some other single deal..
  if (before.secondaryDealIds.length === 0 && after.secondaryDealIds.length === 0) {
    return false;
  }

  // Nothing changed in structure
  if (
    before.primaryDealId === after.primaryDealId &&
    arraysEqualUnordered(before.secondaryDealIds, after.secondaryDealIds)
  ) {
    return false;
  }

  // Something changed in structure
  logger.log('checkIfDealStructureChangedTooMuch - deal structure changed', { before, after });

  return true;
}

/**
 * Update the mission details from the corresponding airtable record.
 * Create sampling specs if loading from shapefiles, i.e. for the first time.
 */
export async function update_mission_details(
  this: Mission,
  { sampling = false, loading_from_shapefiles = false } = {},
): Promise<boolean> {
  if (!this.job_id) {
    const msg = `Unable to update mission details without a job ID`;
    alertWarn(msg);

    throw new Error(msg);
  }

  const missionJob = this.getJob();

  if (!missionJob) {
    throw new Error(`Mission job not found`);
  }

  const atJob = await update_job_details_with_airtable(this, missionJob);

  if (!atJob) {
    throw new Error(`Failed to retrieve job record for job ID ${this.job_id}`);
  }

  await update_job_with_company_details(missionJob, atJob);

  let currentPrimaryDealId: string, currentSecondaryDealIds: string[];

  if (!loading_from_shapefiles) {
    currentPrimaryDealId = this.getPrimarySpec().deal_id;
    currentSecondaryDealIds = this.getSamplingSpecs()
      .filter((spec: SamplingSpec) => spec.deal_id !== currentPrimaryDealId)
      .map((spec: SamplingSpec) => spec.deal_id);
  }

  const deals = await update_job_with_deal(missionJob, atJob);

  if (!deals.length) {
    const mainDealId = atJob.deal?.id ?? '';

    throw new Error(`No active deals were found for this job (looked for ${mainDealId})`);
  }

  if (!loading_from_shapefiles) {
    if (
      checkIfDealStructureChangedTooMuch(
        { primaryDealId: currentPrimaryDealId!, secondaryDealIds: currentSecondaryDealIds! },
        { primaryDealId: deals[0].id, secondaryDealIds: deals.slice(1).map((deal) => deal.id) },
      )
    ) {
      throw new Error('Deal structure changed too much - you have to load from shapefiles');
    }
  }

  update_sample_types(this, atJob);

  if (loading_from_shapefiles) {
    await create_sampling_specs(this, deals);

    // We only want to call this when loading from shapefiles (i.e. loading a mission for the 1st time)
    // because otherwise, a user changes spec parameters (e.g. angle, or cores number), and this function would override it.
    updateMutableSamplingSpecPropsFromAt(this, atJob);
  }

  // Fetch all needed lab records in a single call
  const labsByName = await fetchLabsByName(atJob, deals);

  // Must run before update_mapmaking_rules (which reads lab_instructions)
  await update_job_with_lab_details(missionJob, atJob, labsByName);

  if (!sampling) {
    await update_mapmaking_rules(missionJob, atJob);
  }

  // Should be called after creating specs (or when the specs already exist)
  await updateImmutableSamplingSpecPropsFromAt(this, atJob, deals, labsByName);

  // We should always have a primary spec for the mission.
  if (!this.getPrimarySpec()) {
    throw new Error(`No primary spec found for job ${this.job_id}`);
  }

  return true;
}

export async function renumber_samples(this: Mission, sampling: boolean = true, recalculate_checksum = false) {
  logger.log(`RENUMBER_SAMPLES`, `sampling=${sampling} recalculate_checksum=${recalculate_checksum}`);
  const job = this.getJob();

  if (job && !job.allowed_to_renumber) {
    if (sampling) {
      alertError(
        'Cannot renumber samples while sampling is in progress. Please contacts ops if you need to renumber samples.',
      );

      return;
    }

    // mapmaking mode
    const definitelyRenumber = await alertWarnConfirm(
      'This job does not allow renumbering. Are you SURE you want to renumber?',
    );

    if (!definitelyRenumber) {
      return;
    }
  }

  const samples = cleanSet(flatten(this.getSampleSites().map((site) => site.getSamples())));
  const orderedSamples = orderBy(samples, ['order'], ['asc']);
  const renumberedSamples = new Set<SampleSite>();
  let sampleIndex = 1;

  for (const sample of orderedSamples) {
    const sampleSite = sample.getSampleSite();
    const newSampleId = sampleIndex.toString();

    if (sample.sample_id !== newSampleId) {
      renumberedSamples.add(sampleSite);
    }

    sample.sample_id = sampleIndex.toString();
    sampleIndex++;
  }

  this.last_modified = Date.now() / 1000;

  if (sampling) {
    const session = getCurrentSession();

    for (const sampleSite of renumberedSamples) {
      for (const sample of sampleSite.getSamples()) {
        const box = sample.getSampleBox();

        if (box && box.closed) {
          // set box reprint session ID if this renumbered sample already belonged to a box
          logger.log('RENUMBER_SAMPLES', `Need to reprint box ${box.uid}`);
          box.ReprintSession_id = session?.instance_id;
          box.reprint_reason = 'Samples were renumbered';
        }
      }
    }
  }

  if (recalculate_checksum) {
    await this.to_ros_msg();
  }
}

export async function add_sample(
  this: Mission,
  point: { coords: { lat: number; lon: number } },
  sample_id: string,
  sample_source: SampleSource,
) {
  await logger.log(`ADD_SAMPLE`, `Adding sample ${sample_id} ${sample_source} ${point.coords.lat} ${point.coords.lon}`);
  const sample = Sample.create();
  sample.sample_id = sample_id;

  const sample_site = this.createSampleSite(sample_id);
  sample_site.sample_site_source = sample_source;

  const sample_centroid = SampleCentroid.create();
  sample_centroid.SampleSite_id = sample_site.instance_id;
  sample_centroid.lat = point.coords.lat;
  sample_centroid.lon = point.coords.lon;

  sample.SampleSite_id = sample_site.instance_id;
  sample.sample_type = SamplingTypes.REGULAR;

  const sampling_spec = this.getPrimarySpec();

  if (sampling_spec) {
    sample.SamplingSpec_id = sampling_spec.instance_id;
  }

  const samples_in_site = cleanSet(flattenDeep(this.getSampleSites().map((site) => site.getSamples())));
  sample.order = Math.max(...samples_in_site.map((sel) => sel.order)) + 1;

  return sample;
}

export async function add_core(
  this: Mission,
  point: { coords: { lat: number; lon: number } },
  core_id: string,
  sample_source: SampleSource,
): Promise<SoilCore | undefined> {
  await logger.log(`ADD_CORE`, `Adding core ${core_id} ${sample_source} ${point.coords.lat} ${point.coords.lon}`);

  const { matches, sampleId, coreNumber } = matchCore(core_id);

  if (!matches) {
    alertError(
      `"${core_id}" in invalid, make sure to use alphanumeric characters for "sample ID" and positive numbers for "core #"`,
    );

    return undefined;
  }

  const matchingSamples = this.getAllSamples().filter((sample) => sample.sample_id === sampleId);

  if (!matchingSamples.length) {
    alertWarn(`Sample ${sampleId} does not exist, creating a new sample.`);

    return SoilCore.createCore(this, sampleId, point.coords.lat, point.coords.lon, sample_source, coreNumber);
  }

  const sample = matchingSamples[0];

  if (sample.getSoilCores().find((c) => c.core_number === coreNumber)) {
    alertError(`Core with this number already exists: ${coreNumber}`);

    return undefined;
  }

  return SoilCore.createCore(this, sampleId, point.coords.lat, point.coords.lon, sample_source, coreNumber);
}

export function getBarcodes(this: Mission) {
  const samples = this.getSampleSites().getSamples();
  samples.sort((a, b) => a.sample_id?.localeCompare(b.sample_id || '', 'en', { numeric: true }) || 0);
  const barcodes: string[] = [];

  for (const sample of samples) {
    if (sample.bag_id && !sample.skipped_or_deleted) {
      barcodes.push(sample.bag_id);
    }
  }

  return barcodes;
}

export function getMissionName(this: Mission, type: string) {
  const lastMod = new Date(this.last_modified * 1000);
  const lastModTimestamp = formatDateAsString(lastMod);

  // Attempt to resolve mission name file upload bug by removing more special charcters. This should be more
  // universal across the app but it is not, so this is a bandaid
  return `${this.name}_${lastModTimestamp}_${type}`.replaceAll(':', '-').replaceAll('(', '').replaceAll(')', '');
}

export function getCentroidPath(this: Mission) {
  //let centroidPath: Coordinate[] = [];
  const mission_samples = cleanSet(
    orderBy(flatten(this.getSampleSites().map((site) => site.getSamples())), ['order'], ['asc']),
  );

  return mission_samples.map((sample) => {
    const sample_centroid = sample.getSampleSite().getSampleCentroid();

    if (!sample_centroid) {
      throw new Error(`Sample ${sample.sample_id} does not have a centroid`);
    }

    return { coord: [sample_centroid.lat, sample_centroid.lon] as Coordinate, sample_id: sample.sample_id };
  });
}

export function insertPathWaypoint(coordinate: Coordinate) {}

async function getDealBarcodeType(deal: AtDeal): Promise<AtBarcodeType | null> {
  const dealRestrictedBarcodeType = deal.restrictedBarcodeTypeFinal?.[0];

  if (!dealRestrictedBarcodeType) {
    return null;
  }

  const barcodeTypes = await crmBase.barcodeTypes.get({ formula: `Name = '${dealRestrictedBarcodeType}'` });

  if (!barcodeTypes || barcodeTypes.length === 0) {
    return null;
  }

  return barcodeTypes[0];
}
